import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { msalInstance, loginRequest } from '../config/msalConfig'
import { resolveActiveAccount, needsAccountChoice } from '../config/activeAccount'
import type {
  Tenant,
  AccessMethod,
  CreateTenantDto,
  ConnectionTestResult,
  MFASummary,
  MFAHistoryPoint,
  MFAGlobalAdmin,
  MFAResult,
  MFAUser,
  MFAMethod,
  AuthMethodDetail,
  AuthMethodPolicy,
  PimSnapshot,
  PimSummaryEntry,
  PimRefreshStatus,
  PrivAccessEntry,
  PrivAccessCredential,
  RiskLevel,
  RiskyUser,
  RiskyUserSummaryEntry,
  ScoreSummary,
  ScoreResult,
  ScoreSnapshot,
  SecureScoreControl,
  ControlStatus,
  Me,
  HealthResult,
  Framework,
  AssessmentRun,
  AssessmentSummary,
  PrioritizedFinding,
  Finding,
  GovernanceScore,
  Attestation,
  Baseline,
  BaselineDrift,
  FleetIndicator,
  FleetIndicatorType,
  FleetMatch,
  FleetSummary,
  FleetSweepResult,
} from '../types'
import type { OrgDetail, OrgUser, OrgUserRole, OrgBranding, OrgSummary, AccessDirectoryUser, AccessGrant } from '../types'

// ─── Axios Instance ───────────────────────────────────────────────────────────
// baseURL '/' → same-origin; nginx proxies /api to the backend App Service.

const api: AxiosInstance = axios.create({
  baseURL: '/',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// ─── Auth Interceptors ──────────────────────────────────────────────────────────

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  try {
    // The ACTIVE account only, never the first cached one. With several accounts and none
    // chosen, no token is sent: the 401 below sends the user to the account picker.
    const account = resolveActiveAccount(msalInstance)
    if (account) {
      const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account })
      config.headers.Authorization = `Bearer ${response.accessToken}`
    }
  } catch (error) {
    console.warn('Failed to acquire token silently:', error)
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    // A successful call clears the loop-guard for this session
    sessionStorage.removeItem('auth_redirected')
    return response
  },
  async (error) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true
      const account = resolveActiveAccount(msalInstance)
      if (account) {
        try {
          // Force a fresh token first — handles stale/expired cached tokens
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
            forceRefresh: true,
          })
          original.headers.Authorization = `Bearer ${response.accessToken}`
          return api(original)
        } catch {
          // fall through to a single interactive redirect
        }
      }
      // Redirect to login at most once per session to avoid an infinite loop
      if (!sessionStorage.getItem('auth_redirected')) {
        sessionStorage.setItem('auth_redirected', '1')
        // Several cached accounts and none chosen: let the user pick, never pick for them.
        msalInstance.loginRedirect(needsAccountChoice(msalInstance) ? { ...loginRequest, prompt: 'select_account' } : loginRequest)
      }
    }
    return Promise.reject(error)
  }
)

// ─── Backend wire formats (raw, before adaptation) ─────────────────────────────
// The backend wraps every response in { data, meta? } and uses snake_case fields.

interface Envelope<T> {
  data: T
  meta?: Record<string, unknown>
}

interface RawTenant {
  id: string
  name: string
  tenant_id: string
  domain: string | null
  access_method: 'gdap' | 'dap' | 'service_account' | 'app_consent'
  service_account_upn: string | null
  gdap_relationship_id: string | null
  notes: string | null
  is_active: boolean
  created_at: string
  updated_at: string
  summary?: {
    lastMfaAudit: string | null
    mfaUserCount: number
    mfaEnabledCount: number
    latestSecureScorePct: number | null
  }
}

interface RawMfaSummary {
  tenantId: string
  tenantName: string
  totalUsers: number
  mfaEnabled: number
  mfaDisabled: number
  criticalCount: number
  highCount: number
  mediumCount: number
  lowCount: number
  compliancePercent: number
  auditDate: string | null
}

interface RawMfaUser {
  userId: string
  upn: string
  displayName: string
  isGlobalAdmin: boolean
  mfaEnabled: boolean
  mfaMethods: string[]
  lastSignIn: string | null
  riskLevel: 'none' | 'low' | 'medium' | 'high' | 'critical'
  adminRoles?: string[]
  onPremSyncEnabled?: boolean
  ssprRegistered?: boolean
  methodDetails?: AuthMethodDetail[]
  mfaCapable?: boolean
  mfaRegistered?: boolean
  defaultMfaMethod?: string | null
  passwordlessCapable?: boolean
}

interface RawScoreEntry {
  tenantId: string
  tenantName: string
  score: number
  maxScore: number
  percentage: number
  snapshotDate: string | null
  trend?: { score: number; maxScore: number; percentage: number; snapshotDate: string }[]
}

interface RawScoreDetail {
  tenantId: string
  tenantName: string
  score: number
  maxScore: number
  percentage: number
  createdDateTime: string
  controlScores: {
    controlName: string; score: number; maxScore: number; implementationStatus: string; description?: string
    title?: string; remediation?: string; implementationCost?: string; userImpact?: string; service?: string; deepLink?: string
  }[]
}

interface RawScoreHistory {
  snapshotDate: string
  score: number
  maxScore: number
  percentage: number
}

// ─── Mappers ───────────────────────────────────────────────────────────────────

const ACCESS_MAP: Record<string, AccessMethod> = { gdap: 'GDAP', dap: 'DAP', service_account: 'service_account', app_consent: 'app_consent' }
const ACCESS_MAP_REV: Record<string, string> = { GDAP: 'gdap', DAP: 'dap', service_account: 'service_account' }
const RISK_MAP: Record<string, RiskLevel> = { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', low: 'LOW', none: 'LOW' }
// Maps a Graph authentication-method @odata.type to a display label. Only real
// MFA methods are listed here; password and email-OTP (SSPR-only) are excluded.
const METHOD_MAP: Record<string, MFAMethod> = {
  '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod': 'Authenticator app',
  '#microsoft.graph.softwareOathAuthenticationMethod': 'Software OATH',
  '#microsoft.graph.phoneAuthenticationMethod': 'Phone (SMS/Call)',
  '#microsoft.graph.fido2AuthenticationMethod': 'FIDO2 key',
  '#microsoft.graph.windowsHelloForBusinessAuthenticationMethod': 'Windows Hello',
  '#microsoft.graph.temporaryAccessPassAuthenticationMethod': 'Temporary Access Pass',
  '#microsoft.graph.x509CertificateAuthenticationMethod': 'Certificate',
}

const PHONE_METHOD = '#microsoft.graph.phoneAuthenticationMethod'

// Auth-method types that Graph returns but that do NOT constitute MFA.
const NON_MFA_METHODS = new Set<string>([
  '#microsoft.graph.passwordAuthenticationMethod',
  '#microsoft.graph.emailAuthenticationMethod',
])

// Real MFA method @odata.types after dropping the non-MFA ones.
function realMfaTypes(methods: string[]): string[] {
  return (methods ?? []).filter((m) => !NON_MFA_METHODS.has(m))
}

function mapMethods(methods: string[]): MFAMethod[] {
  const mapped = realMfaTypes(methods).map((m) => METHOD_MAP[m] ?? 'Other')
  const unique = Array.from(new Set(mapped))
  return unique.length === 0 ? ['None'] : unique
}

function mapControlStatus(s: string): ControlStatus {
  const v = (s || '').toLowerCase()
  if (v.includes('partial') || v.includes('progress')) return 'inProgress'
  if (v.includes('notapplicable') || v.includes('not applicable')) return 'notApplicable'
  if (v.includes('not')) return 'notImplemented'
  if (v.includes('implemented')) return 'implemented'
  return 'notApplicable'
}

function adaptTenant(t: RawTenant): Tenant {
  return {
    id: t.id,
    name: t.name,
    tenantId: t.tenant_id,
    primaryDomain: t.domain ?? '',
    accessMethod: ACCESS_MAP[t.access_method] ?? 'service_account',
    gdapRelationshipId: t.gdap_relationship_id ?? undefined,
    serviceAccountUpn: t.service_account_upn ?? undefined,
    notes: t.notes ?? undefined,
    status: t.is_active === false ? 'inactive' : 'active',
    lastAuditedAt: t.summary?.lastMfaAudit ?? undefined,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
  }
}

function adaptMfaUser(u: RawMfaUser): MFAUser {
  return {
    id: u.userId,
    displayName: u.displayName,
    upn: u.upn,
    isGlobalAdmin: u.isGlobalAdmin,
    mfaEnabled: u.mfaEnabled,
    mfaMethods: mapMethods(u.mfaMethods),
    riskLevel: RISK_MAP[u.riskLevel] ?? 'LOW',
    lastSignIn: u.lastSignIn ?? undefined,
    adminRoles: u.adminRoles ?? [],
    onPremSyncEnabled: u.onPremSyncEnabled,
    ssprRegistered: u.ssprRegistered,
    methodDetails: u.methodDetails ?? [],
    mfaCapable: u.mfaCapable,
    mfaRegistered: u.mfaRegistered,
    defaultMfaMethod: u.defaultMfaMethod ?? null,
    passwordlessCapable: u.passwordlessCapable,
  }
}

function toBackendTenant(d: Partial<CreateTenantDto>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (d.name !== undefined) out['name'] = d.name
  if (d.tenantId !== undefined) out['tenant_id'] = d.tenantId
  if (d.primaryDomain !== undefined) out['domain'] = d.primaryDomain
  if (d.accessMethod !== undefined) out['access_method'] = ACCESS_MAP_REV[d.accessMethod] ?? d.accessMethod
  if (d.gdapRelationshipId !== undefined) out['gdap_relationship_id'] = d.gdapRelationshipId
  if (d.serviceAccountUpn !== undefined) out['service_account_upn'] = d.serviceAccountUpn
  if (d.keyVaultSecretName !== undefined) out['service_account_secret_name'] = d.keyVaultSecretName
  if (d.notes !== undefined) out['notes'] = d.notes
  return out
}

// ─── Tenant API ───────────────────────────────────────────────────────────────

export const getTenants = (opts?: { includeInactive?: boolean }) =>
  api
    .get<Envelope<RawTenant[]>>(`/api/tenants${opts?.includeInactive ? '?includeInactive=1' : ''}`)
    .then((r) => (r.data.data ?? []).map(adaptTenant))

export const getTenant = (id: string) =>
  api.get<Envelope<RawTenant>>(`/api/tenants/${id}`).then((r) => adaptTenant(r.data.data))

export const createTenant = (data: CreateTenantDto) =>
  api.post<Envelope<RawTenant>>('/api/tenants', toBackendTenant(data)).then((r) => adaptTenant(r.data.data))

export const updateTenant = (id: string, data: Partial<CreateTenantDto>) =>
  api.put<Envelope<RawTenant>>(`/api/tenants/${id}`, toBackendTenant(data)).then((r) => adaptTenant(r.data.data))

export const deleteTenant = (id: string) =>
  api.delete(`/api/tenants/${id}`).then((r) => r.data)

/** PERMANENT tenant removal (SuperAdmin only) — deletes the tenant and all its data. */
export const purgeTenant = (id: string): Promise<{ rowsDeleted: number }> =>
  api.delete<Envelope<{ rowsDeleted: number }>>(`/api/tenants/${id}/purge`).then((r) => r.data.data)

export const deleteAssessmentRun = (runId: string): Promise<void> =>
  api.delete(`/api/assessments/run/${runId}`).then(() => undefined)

export const testTenantConnection = (id: string): Promise<ConnectionTestResult> =>
  api
    .post<Envelope<{ success: boolean }>>(`/api/tenants/${id}/test-connection`)
    .then((r) => ({ success: r.data.data.success, message: 'Connection successful' }))
    .catch((e) => ({ success: false, message: e?.response?.data?.message ?? 'Connection failed' }))

// ─── MFA API ──────────────────────────────────────────────────────────────────

export const getMFASummary = (): Promise<MFASummary> =>
  api.get<Envelope<RawMfaSummary[]>>('/api/mfa/summary').then((r) => {
    const arr = r.data.data ?? []
    const sum = (f: (s: RawMfaSummary) => number) => arr.reduce((a, s) => a + f(s), 0)
    return {
      totalUsersAudited: sum((s) => s.totalUsers),
      mfaEnabledTotal: sum((s) => s.mfaEnabled),
      noMfaTotal: sum((s) => s.mfaDisabled),
      criticalCount: sum((s) => s.criticalCount),
      highCount: sum((s) => s.highCount),
      mediumCount: sum((s) => s.mediumCount),
      tenantBreakdown: arr.map((s) => ({
        tenantId: s.tenantId,
        tenantName: s.tenantName,
        compliancePercent: s.compliancePercent,
      })),
    }
  })

// Returns a single-element array so callers can use `.data?.[0]` (matches MFAAudit/TenantDetail usage)
export const getTenantMFA = (tenantId: string): Promise<MFAResult[]> =>
  api
    .get<Envelope<{ tenantId: string; tenantName: string; auditDate: string; users: RawMfaUser[]; summary: RawMfaSummary }>>(`/api/mfa/${tenantId}`)
    .then((r) => {
      const d = r.data.data
      const rawUsers = d.users ?? []
      const users = rawUsers.map(adaptMfaUser)
      // SMS-only = MFA on, and the only *real* MFA method is phone. Filter out
      // non-MFA types (password/email) first, otherwise this is always 0.
      const smsOnly = rawUsers.filter((u) => {
        const real = realMfaTypes(u.mfaMethods)
        return u.mfaEnabled && real.length > 0 && real.every((m) => m === PHONE_METHOD)
      }).length
      const result: MFAResult = {
        tenantId: d.tenantId,
        tenantName: d.tenantName,
        tenantDomain: '',
        auditedAt: d.auditDate,
        totalUsers: d.summary?.totalUsers ?? users.length,
        mfaEnabledCount: d.summary?.mfaEnabled ?? users.filter((u) => u.mfaEnabled).length,
        noMfaCount: d.summary?.mfaDisabled ?? users.filter((u) => !u.mfaEnabled).length,
        smsOnlyCount: smsOnly,
        compliancePercent: d.summary?.compliancePercent ?? 0,
        users,
      }
      return [result]
    })

export interface MFARefreshOutcome {
  at: string
  userCount: number
  error: string | null
}
export interface MFARefreshStarted {
  tenantId: string
  status: 'started' | 'already_running'
  last: MFARefreshOutcome | null
}
export interface MFARefreshStatus {
  tenantId: string
  running: boolean
  last: MFARefreshOutcome | null
}

// The audit runs in the BACKGROUND: a first pull for a new tenant makes one Graph
// call per user and can take minutes — far longer than any client/proxy timeout —
// so this POST returns 202 immediately. Poll getMFARefreshStatus for completion.
export const refreshMFA = (tenantId: string): Promise<MFARefreshStarted> =>
  api.post<Envelope<MFARefreshStarted>>(`/api/mfa/${tenantId}/refresh`).then((r) => r.data.data)

export const getMFARefreshStatus = (tenantId: string): Promise<MFARefreshStatus> =>
  api.get<Envelope<MFARefreshStatus>>(`/api/mfa/${tenantId}/refresh/status`).then((r) => r.data.data)

// Tenant authentication-method policy posture (one live Graph call, cached by
// TanStack Query on the page). Backend returns camelCase already.
export const getMFAMethodPolicy = (tenantId: string): Promise<AuthMethodPolicy> =>
  api.get<Envelope<AuthMethodPolicy>>(`/api/mfa/${tenantId}/method-policy`).then((r) => r.data.data)

// ── PIM / Privileged Access ─────────────────────────────────────────────────────
// Backend returns camelCase (pimService / privilegedRegisterService), so no adapt.
export const getPim = (tenantId: string): Promise<PimSnapshot> =>
  api.get<Envelope<PimSnapshot>>(`/api/pim/${tenantId}`).then((r) => r.data.data)

export const getPimSummary = (): Promise<PimSummaryEntry[]> =>
  api.get<Envelope<PimSummaryEntry[]>>(`/api/pim/summary`).then((r) => r.data.data ?? [])

export const refreshPimTenant = (tenantId: string): Promise<{ tenantId: string; status: string }> =>
  api.post<Envelope<{ tenantId: string; status: string }>>(`/api/pim/${tenantId}/refresh`).then((r) => r.data.data)

export const getPimRefreshStatus = (tenantId: string): Promise<PimRefreshStatus> =>
  api.get<Envelope<PimRefreshStatus>>(`/api/pim/${tenantId}/refresh/status`).then((r) => r.data.data)

// Manual register CRUD
export const listRegisterEntries = (tenantId?: string): Promise<PrivAccessEntry[]> =>
  api.get<Envelope<PrivAccessEntry[]>>(`/api/privileged-register${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`).then((r) => r.data.data ?? [])

export const getRegisterEntry = (id: string): Promise<PrivAccessEntry> =>
  api.get<Envelope<PrivAccessEntry>>(`/api/privileged-register/${id}`).then((r) => r.data.data)

export const createRegisterEntry = (body: Partial<PrivAccessEntry> & { tenantId: string; platform: string }): Promise<PrivAccessEntry> =>
  api.post<Envelope<PrivAccessEntry>>(`/api/privileged-register`, body).then((r) => r.data.data)

export const updateRegisterEntry = (id: string, body: Partial<PrivAccessEntry>): Promise<PrivAccessEntry> =>
  api.patch<Envelope<PrivAccessEntry>>(`/api/privileged-register/${id}`, body).then((r) => r.data.data)

export const deleteRegisterEntry = (id: string): Promise<void> =>
  api.delete(`/api/privileged-register/${id}`).then(() => undefined)

export const addRegisterCredential = (entryId: string, body: Partial<PrivAccessCredential>): Promise<PrivAccessCredential> =>
  api.post<Envelope<PrivAccessCredential>>(`/api/privileged-register/${entryId}/credentials`, body).then((r) => r.data.data)

export const updateRegisterCredential = (credId: string, body: Partial<PrivAccessCredential>): Promise<void> =>
  api.patch(`/api/privileged-register/credentials/${credId}`, body).then(() => undefined)

export const deleteRegisterCredential = (credId: string): Promise<void> =>
  api.delete(`/api/privileged-register/credentials/${credId}`).then(() => undefined)

// History returns the most recent audit batches (newest first); reverse to ascending for charts.
export const getMFAHistory = (tenantId: string): Promise<MFAHistoryPoint[]> =>
  api.get<Envelope<RawMfaSummary[]>>(`/api/mfa/${tenantId}/history`).then((r) =>
    (r.data.data ?? [])
      .slice()
      .reverse()
      .filter((s) => s.auditDate)
      .map((s) => ({
        auditDate: s.auditDate as string,
        compliancePercent: s.compliancePercent,
        totalUsers: s.totalUsers,
        mfaEnabled: s.mfaEnabled,
        noMfa: s.mfaDisabled,
        criticalCount: s.criticalCount,
      }))
  )

interface RawMfaGlobalAdmin {
  tenantId: string
  tenantName: string
  userId: string
  upn: string
  displayName: string | null
  mfaEnabled: boolean
  mfaMethods: string[]
  riskLevel: 'none' | 'low' | 'medium' | 'high' | 'critical'
  lastSignIn: string | null
  auditDate: string
  adminRoles?: string[]
  onPremSyncEnabled?: boolean
  ssprRegistered?: boolean | null
  methodDetails?: AuthMethodDetail[]
}

// Cross-tenant privileged admins with roles + full MFA detail (from latest audits).
export const getMFAGlobalAdmins = (): Promise<MFAGlobalAdmin[]> =>
  api.get<Envelope<RawMfaGlobalAdmin[]>>('/api/mfa/global-admins').then((r) =>
    (r.data.data ?? []).map((a) => ({
      userId: a.userId,
      upn: a.upn,
      displayName: a.displayName ?? a.upn,
      tenantId: a.tenantId,
      tenantName: a.tenantName,
      mfaEnabled: a.mfaEnabled,
      mfaMethods: mapMethods(a.mfaMethods),
      riskLevel: RISK_MAP[a.riskLevel] ?? 'LOW',
      lastSignIn: a.lastSignIn ?? undefined,
      adminRoles: a.adminRoles ?? [],
      onPremSyncEnabled: a.onPremSyncEnabled ?? false,
      ssprRegistered: a.ssprRegistered ?? null,
      methodDetails: a.methodDetails ?? [],
    }))
  )

// ─── Secure Score API ─────────────────────────────────────────────────────────

/** Posture windows the dashboard can request (112662). Must stay in step with
 *  ALLOWED_RANGE_DAYS in backend/src/routes/securescore.ts — the backend 400s on
 *  anything else rather than silently substituting a different range. */
export type PostureRangeDays = 7 | 14 | 30 | 90 | 180

export const POSTURE_RANGES: ReadonlyArray<{ days: PostureRangeDays; label: string }> = [
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
]

export const DEFAULT_POSTURE_RANGE: PostureRangeDays = 30

export const getScoreSummary = (days?: PostureRangeDays): Promise<ScoreSummary> =>
  api.get<Envelope<RawScoreEntry[]>>('/api/securescore/summary', {
    params: days ? { days } : undefined,
  }).then((r) => {
    const entries = r.data.data ?? []
    const meta = (r.data.meta ?? {}) as { averagePercentage?: number; totalTenants?: number }
    const pcts = entries.map((e) => e.percentage)
    return {
      averageScore: meta.averagePercentage ?? (pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : 0),
      highestScore: pcts.length ? Math.max(...pcts) : 0,
      lowestScore: pcts.length ? Math.min(...pcts) : 0,
      totalTenants: meta.totalTenants ?? entries.length,
      tenantScores: entries.map((e) => ({
        tenantId: e.tenantId,
        tenantName: e.tenantName,
        percentage: e.percentage,
        currentScore: e.score,
        maxScore: e.maxScore,
        trend: (e.trend ?? []).map((t) => ({
          date: t.snapshotDate,
          score: t.score,
          maxScore: t.maxScore,
          percentage: t.percentage,
        })) as ScoreSnapshot[],
      })),
    }
  })

// Fallback for snapshots stored before profile enrichment: turn a raw control id
// like `spo_idle_session_timeout` / `AATP_DefenderForIdentityIsNotInstalled`
// into a readable heading. Enriched controls already carry a proper `title`.
const CONTROL_PREFIX: Record<string, string> = {
  spo: 'SharePoint', exo: 'Exchange', mip: 'Purview', aad: 'Entra ID',
  aatp: 'Defender for Identity', mdo: 'Defender for Office', mde: 'Defender for Endpoint',
}
function prettifyControlId(id: string): string {
  if (!id) return 'Unknown control'
  const parts = id.split(/[_\s]+/).filter(Boolean)
  const prefix = parts[0]?.toLowerCase() ?? ''
  const label = CONTROL_PREFIX[prefix]
  const rest = (label ? parts.slice(1) : parts)
    // split camelCase tokens (DefenderForIdentityIsNotInstalled → Defender For Identity …)
    .flatMap((p) => p.replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(' '))
    .filter(Boolean)
  const sentence = rest.join(' ').toLowerCase().replace(/^./, (ch) => ch.toUpperCase())
  return label ? `${label}: ${sentence}` : sentence || id
}

export const getTenantScore = (tenantId: string): Promise<ScoreResult> =>
  api.get<Envelope<RawScoreDetail>>(`/api/securescore/${tenantId}`).then((r) => {
    const d = r.data.data
    const controls: SecureScoreControl[] = (d.controlScores ?? []).map((c, i) => ({
      id: String(i),
      name: c.title || prettifyControlId(c.controlName),
      category: c.service || 'General',
      currentScore: c.score,
      maxScore: c.maxScore,
      status: mapControlStatus(c.implementationStatus),
      description: c.description,
      remediation: c.remediation,
      implementationCost: c.implementationCost,
      userImpact: c.userImpact,
      service: c.service,
      deepLink: c.deepLink,
    }))
    return {
      tenantId: d.tenantId,
      tenantName: d.tenantName,
      currentScore: d.score,
      maxScore: d.maxScore,
      percentage: d.percentage,
      lastUpdated: d.createdDateTime,
      controls,
      history: [],
    }
  })

export const refreshTenantScore = (tenantId: string) =>
  api.post(`/api/securescore/${tenantId}/refresh`).then((r) => r.data)

// `days` is the posture window (112662). Omitting it keeps the backend default
// of 30, so existing callers are unaffected.
export const getTenantScoreHistory = (tenantId: string, days?: PostureRangeDays): Promise<ScoreSnapshot[]> =>
  api
    .get<Envelope<RawScoreHistory[]>>(`/api/securescore/${tenantId}/history`, {
      params: days ? { days } : undefined,
    })
    .then((r) =>
      (r.data.data ?? []).map((h) => ({
        date: h.snapshotDate,
        score: h.score,
        maxScore: h.maxScore,
        percentage: h.percentage,
      }))
    )

// ─── Risky users (Identity Protection) ────────────────────────────────────────

export interface RunnerReadiness { ok: boolean; status: 'ok' | 'pending' | 'action_required'; detail: string }

/** Live prerequisite check for the advanced posture checks in one tenant. The failure
 *  banner is built from a run's stored error, so this is what tells us it is now stale. */
export const getRunnerReadiness = (tenantId: string): Promise<RunnerReadiness> =>
  api
    .get<Envelope<RunnerReadiness>>(`/api/assessments/${tenantId}/runner-readiness`)
    .then((r) => r.data.data)

export const getRiskyUsersSummary = (): Promise<RiskyUserSummaryEntry[]> =>
  api.get<Envelope<RiskyUserSummaryEntry[]>>('/api/riskyusers/summary').then((r) => r.data.data ?? [])

export const getTenantRiskyUsers = (tenantId: string): Promise<RiskyUser[]> =>
  api
    .get<Envelope<{ tenantId: string; tenantName: string; users: RiskyUser[] }>>(`/api/riskyusers/${tenantId}`)
    .then((r) => r.data.data.users ?? [])

/** Why detection detail may be absent (112669). */
export type DetectionAvailability = 'ok' | 'not_consented' | 'licence_limited' | 'error'

export interface ResolvedRiskyUser {
  id: string
  userPrincipalName: string
  displayName: string
  riskLevel: string
  riskState: string
  riskDetail: string
  resolvedObservedAt: string
  lastUpdated: string | null
}

export interface TenantRiskyUsers {
  users: RiskyUser[]
  fetchedAt: string | null
  /** Users moved to a resolved state in the trailing 30 days (112666). */
  remediated30d: number
  resolved: ResolvedRiskyUser[]
  resolvedStates: string[]
  detectionAvailability: DetectionAvailability
  detectionsError: string | null
}

/** Full payload including the remediated count and resolved list (112666/112668/112669). */
export const getTenantRiskyUsersDetail = (tenantId: string): Promise<TenantRiskyUsers> =>
  api
    .get<Envelope<{
      users: RiskyUser[]
      fetchedAt: string | null
      remediated30d: number
      resolved: ResolvedRiskyUser[]
      resolvedStates: string[]
      detectionAvailability: DetectionAvailability
      detectionsError: string | null
    }>>(`/api/riskyusers/${tenantId}`)
    .then((r) => {
      const d = r.data.data
      return {
        users: d.users ?? [],
        fetchedAt: d.fetchedAt ?? null,
        remediated30d: d.remediated30d ?? 0,
        resolved: d.resolved ?? [],
        resolvedStates: d.resolvedStates ?? [],
        detectionAvailability: d.detectionAvailability ?? 'ok',
        detectionsError: d.detectionsError ?? null,
      }
    })

export const refreshTenantRiskyUsers = (tenantId: string) =>
  api.post(`/api/riskyusers/${tenantId}/refresh`).then((r) => r.data)

// ─── Auth / RBAC ────────────────────────────────────────────────────────────

export const getMe = (): Promise<Me> =>
  api.get<Envelope<Me>>('/api/auth/me').then((r) => r.data.data)

// ─── Onboarding ───────────────────────────────────────────────────────────────

export const startConsent = (name: string): Promise<{ consentUrl: string }> =>
  api.post<Envelope<{ consentUrl: string }>>('/api/onboarding/consent/start', { name }).then((r) => r.data.data)

export const createServiceAccountTenant = (input: {
  name: string
  tenant_id: string
  service_account_upn: string
  service_account_secret_name: string
  domain?: string
}): Promise<Tenant> =>
  api.post<Envelope<RawTenant>>('/api/onboarding/service-account', input).then((r) => adaptTenant(r.data.data))

export const runTenantHealthCheck = (tenantId: string): Promise<HealthResult> =>
  api.post<Envelope<HealthResult>>(`/api/onboarding/${tenantId}/health`).then((r) => r.data.data)

// ─── Assessments & baselines ────────────────────────────────────────────────

interface RawRun {
  id: string; tenant_id: string; framework_id: string; framework_name: string | null
  score: number | null; total_controls: number | null
  pass_count: number; fail_count: number; partial_count: number; skipped_count: number; not_tested_count: number; attestation_count?: number | null
  org_name: string | null; tenant_visible: number | null; created_at: string
  status?: string | null; engine?: string | null
  enrichment?: {
    category: 'config' | 'runner' | 'ingest' | 'timeout' | 'prerequisite' | 'graph' | null
    detail: string | null
    retryable: boolean
  }
}
interface RawFinding {
  id: string; control_ref: string | null; test_id: string | null; maester_id: string | null
  pillar: string | null; severity: string | null; outcome: Finding['outcome']; status_label: string | null
  requirement: string | null; how_to_achieve: string | null; current_state: string | null; data_source: string | null
  metric_value: number | null; metric_label: string | null; attested?: boolean
  skip_reason?: string | null
}
interface RawBaseline {
  id: string; tenant_id: string | null; name: string; source_framework: string | null
  description: string | null; is_template: number; is_active: number; created_at: string
}
interface RawDrift {
  id: string; baseline_id: string; run_id: string; drift_count: number | null
  conformant_count: number | null; conformance_pct: number | null; details_json: string | null; computed_at: string
}

const adaptRun = (r: RawRun): AssessmentRun => ({
  id: r.id, tenantId: r.tenant_id, frameworkId: r.framework_id, frameworkName: r.framework_name,
  score: r.score, totalControls: r.total_controls, passCount: r.pass_count ?? 0, failCount: r.fail_count ?? 0,
  partialCount: r.partial_count ?? 0, skippedCount: r.skipped_count ?? 0, notTestedCount: r.not_tested_count ?? 0,
  attestationCount: r.attestation_count ?? 0,
  orgName: r.org_name, tenantVisible: r.tenant_visible === 1, createdAt: r.created_at,
  status: r.status ?? undefined, engine: r.engine ?? undefined,
  // Why advanced posture checks did not complete. `detail` is null for customers —
  // the backend only populates it for internal users.
  enrichment: r.enrichment ?? undefined,
})
const adaptFinding = (f: RawFinding): Finding => ({
  id: f.id, controlRef: f.control_ref, testId: f.test_id, maesterId: f.maester_id, pillar: f.pillar,
  severity: f.severity, outcome: f.outcome, statusLabel: f.status_label, requirement: f.requirement,
  howToAchieve: f.how_to_achieve, currentState: f.current_state, dataSource: f.data_source,
  metricValue: f.metric_value ?? null, metricLabel: f.metric_label ?? null,
  attested: f.attested ?? false,
  skipReason: f.skip_reason ?? null,
})
const adaptBaseline = (b: RawBaseline): Baseline => ({
  id: b.id, tenantId: b.tenant_id, name: b.name, sourceFramework: b.source_framework,
  description: b.description, isTemplate: b.is_template === 1, isActive: b.is_active === 1, createdAt: b.created_at,
})
const adaptDrift = (d: RawDrift): BaselineDrift => ({
  id: d.id, baselineId: d.baseline_id, runId: d.run_id, driftCount: d.drift_count,
  conformantCount: d.conformant_count, conformancePct: d.conformance_pct,
  details: d.details_json ? JSON.parse(d.details_json) : [], computedAt: d.computed_at,
})

export const getFrameworks = (): Promise<Framework[]> =>
  api.get<Envelope<Framework[]>>('/api/frameworks').then((r) => r.data.data ?? [])

export const runAssessment = (tenantId: string, framework: string): Promise<AssessmentRun> =>
  api.post<Envelope<RawRun>>(`/api/assessments/${tenantId}/run?framework=${encodeURIComponent(framework)}`).then((r) => adaptRun(r.data.data))

interface RawAssessmentSummaryEntry {
  tenantId: string; tenantName: string; frameworkId: string | null; frameworkName: string | null
  score: number | null; totalControls: number | null; passCount: number; failCount: number
  partialCount: number; attestationCount: number; status: string | null; createdAt: string | null
  trend: { score: number; createdAt: string }[]
}

export const getAssessmentSummary = (): Promise<AssessmentSummary> =>
  api.get<Envelope<RawAssessmentSummaryEntry[]>>('/api/assessments/summary').then((r) => {
    const entries = (r.data.data ?? []) as RawAssessmentSummaryEntry[]
    const meta = (r.data.meta ?? {}) as { averageScore?: number; totalTenants?: number; tenantsWithData?: number }
    return {
      averageScore: meta.averageScore ?? 0,
      totalTenants: meta.totalTenants ?? entries.length,
      tenantsWithData: meta.tenantsWithData ?? entries.filter((e) => e.score != null).length,
      entries: entries.map((e) => ({ ...e, trend: e.trend ?? [] })),
    }
  })

export const getPrioritizedFindings = (): Promise<PrioritizedFinding[]> =>
  api.get<Envelope<PrioritizedFinding[]>>('/api/assessments/findings/prioritized').then((r) => r.data.data ?? [])

// ─── Entitlements admin (E0) ────────────────────────────────────────────────

export interface PlanInfo {
  id: string; key: string; name: string; tier: string | null
  seat_band_min: number | null; seat_band_max: number | null; is_default: number
  features: Record<string, boolean>
}
export const getPlans = (): Promise<PlanInfo[]> =>
  api.get<Envelope<PlanInfo[]>>('/api/admin/plans').then((r) => r.data.data ?? [])

// Assign a plan (+ optional seat band) to a customer org. SuperAdmin only.
export const setOrgSubscription = (
  orgId: string,
  body: { planKey: string; seatBand: number | null }
): Promise<void> =>
  api.put(`/api/admin/orgs/${orgId}/subscription`, body).then(() => undefined)

export interface SeatMeasure {
  tenantId: string; tenantName: string; measuredSeats: number | null; asOf: string | null; orgId: string | null
}
export const getSeatMeasures = (): Promise<SeatMeasure[]> =>
  api.get<Envelope<SeatMeasure[]>>('/api/admin/seat-measures').then((r) => r.data.data ?? [])

// ── E2: Customer-org management (per-org quotas, branding, users) ───────────
export const listOrgs = (): Promise<OrgSummary[]> =>
  api.get<Envelope<OrgSummary[]>>('/api/orgs').then((r) => r.data.data ?? [])

export const createOrg = (body: {
  name: string
  slug?: string
  maxTenants?: number | null
  maxAssessmentsPerMonth?: number | null
}): Promise<OrgDetail> =>
  api.post<Envelope<OrgDetail>>('/api/orgs', body).then((r) => r.data.data)

export const getOrg = (orgId: string): Promise<OrgDetail> =>
  api.get<Envelope<OrgDetail>>(`/api/orgs/${orgId}`).then((r) => r.data.data)

export const mapOrgTenant = (orgId: string, tenantId: string): Promise<OrgDetail> =>
  api.post<Envelope<OrgDetail>>(`/api/orgs/${orgId}/tenants`, { tenantId }).then((r) => r.data.data)

export const unmapOrgTenant = (orgId: string, tenantId: string): Promise<void> =>
  api.delete(`/api/orgs/${orgId}/tenants/${tenantId}`).then(() => undefined)

export const setOrgFeatures = (orgId: string, features: Record<string, boolean>): Promise<OrgDetail> =>
  api.put<Envelope<OrgDetail>>(`/api/orgs/${orgId}/features`, { features }).then((r) => r.data.data)

// Approve a self-onboarded (pending) org: enable the portal + mark active.
export const approveOrg = (orgId: string): Promise<OrgDetail> =>
  api.post<Envelope<OrgDetail>>(`/api/orgs/${orgId}/approve`, {}).then((r) => r.data.data)

/** Delete an organization (SuperAdmin). Tenants are unmapped, never deleted. */
export const deleteOrg = (orgId: string): Promise<void> =>
  api.delete(`/api/orgs/${orgId}`).then(() => undefined)

// ─── Self-service onboarding: invites (internal) + public signup calls ──────
export interface InviteInfo {
  id: string
  orgName: string
  email: string | null
  status: string
  createdAt: string
  expiresAt: string | null
  consumedAt: string | null
  signupUrl: string | null
}
export const listInvites = (): Promise<InviteInfo[]> =>
  api.get<Envelope<InviteInfo[]>>('/api/onboarding/invites').then((r) => r.data.data ?? [])
export const createInvite = (body: { orgName: string; email?: string; expectedTenantId?: string; expiresInDays?: number }): Promise<InviteInfo> =>
  api.post<Envelope<InviteInfo>>('/api/onboarding/invites', body).then((r) => r.data.data)
export const revokeInvite = (id: string): Promise<void> =>
  api.delete(`/api/onboarding/invites/${id}`).then(() => undefined)

// Public (unauthenticated) — used by the /signup page. The axios interceptor sends no
// token when there's no signed-in account, which these public endpoints accept.
export const validateInvite = (token: string): Promise<{ orgName: string; email: string | null; expiresAt: string | null }> =>
  api.get<Envelope<{ orgName: string; email: string | null; expiresAt: string | null }>>(`/api/onboarding/invite/${encodeURIComponent(token)}`).then((r) => r.data.data)
export const startSelfConsent = (token: string): Promise<{ consentUrl: string }> =>
  api.post<Envelope<{ consentUrl: string }>>('/api/onboarding/self/consent/start', { token }).then((r) => r.data.data)

// ── Customer-initiated self-service signup (public, no auth) ─────────────────
export interface SignupPermission {
  scope: string
  purpose: string
  /** Present for non-Graph resources; `.default` consent grants those too. */
  resource?: string
  /** Set when the scope is not a read scope — shown so the "read-only" claim is explained. */
  readOnlyCaveat?: string
}
/** The step admin consent cannot perform — assigning a directory role. Server-supplied
 *  so the app id and role template match what the consent URL was built for. */
export interface SignupPostConsentStep {
  title: string
  why: string
  portalPath: string
  script: string
  roleName: string
  roleTemplateId: string
  appId: string
}
export interface SignupPermissions {
  version: string
  readOnly: boolean
  permissions: SignupPermission[]
  postConsent: SignupPostConsentStep
}
export interface SelfSignupPayload {
  orgName: string
  contactName: string
  contactEmail: string
  tenantDomain?: string
  expectedTenantId?: string
  acceptedPermissions: true
  permissionsVersion: string
}
export interface SelfSignupResult { consentUrl: string; orgId: string; emailSent: boolean; emailConfigured: boolean }

export const getSignupPermissions = (): Promise<SignupPermissions> =>
  api.get<Envelope<SignupPermissions>>('/api/onboarding/self/permissions').then((r) => r.data.data)
export const submitSelfSignup = (body: SelfSignupPayload): Promise<SelfSignupResult> =>
  api.post<Envelope<SelfSignupResult>>('/api/onboarding/self/signup', body).then((r) => r.data.data)

export const getOrgByTenant = (tenantId: string): Promise<OrgDetail | null> =>
  api.get<Envelope<OrgDetail | null>>(`/api/orgs/by-tenant/${tenantId}`).then((r) => r.data.data)

export const updateOrg = (
  orgId: string,
  body: {
    name?: string
    maxTenants?: number | null
    maxAssessmentsPerMonth?: number | null
    portalEnabled?: boolean
    branding?: OrgBranding | null
  }
): Promise<OrgDetail> =>
  api.patch<Envelope<OrgDetail>>(`/api/orgs/${orgId}`, body).then((r) => r.data.data)

export const getOrgUsers = (orgId: string): Promise<OrgUser[]> =>
  api.get<Envelope<OrgUser[]>>(`/api/orgs/${orgId}/users`).then((r) => r.data.data ?? [])

export const inviteOrgUser = (orgId: string, body: { email: string; role: OrgUserRole }): Promise<OrgUser> =>
  api.post<Envelope<OrgUser>>(`/api/orgs/${orgId}/users`, body).then((r) => r.data.data)

export const updateOrgUserRole = (orgId: string, userId: string, role: OrgUserRole): Promise<void> =>
  api.patch(`/api/orgs/${orgId}/users/${userId}`, { role }).then(() => undefined)

export const removeOrgUser = (orgId: string, userId: string): Promise<void> =>
  api.delete(`/api/orgs/${orgId}/users/${userId}`).then(() => undefined)

/** SuperAdmin: users of one of the org's tenants, with their role in the org. */
export const getOrgDirectory = (orgId: string, tenantId: string, q: string): Promise<AccessDirectoryUser[]> =>
  api
    .get<Envelope<AccessDirectoryUser[]>>(`/api/orgs/${orgId}/directory`, {
      params: q.trim() ? { tenantId, q: q.trim() } : { tenantId },
    })
    .then((r) => r.data.data ?? [])

/** SuperAdmin: give a directory user an org role (linked on first sign-in, unlike an email invite). */
export const addOrgUserFromDirectory = (
  orgId: string,
  body: { tenantId: string; oid: string; role: OrgUserRole }
): Promise<void> => api.post(`/api/orgs/${orgId}/users/directory`, body).then(() => undefined)

// ── Access management (customer Global Admins only; org + tenant come from the session) ──

export const getAccessDirectory = (q: string): Promise<AccessDirectoryUser[]> =>
  api
    .get<Envelope<AccessDirectoryUser[]>>('/api/access/users', { params: q.trim() ? { q: q.trim() } : {} })
    .then((r) => r.data.data ?? [])

export const getAccessGrants = (): Promise<AccessGrant[]> =>
  api.get<Envelope<AccessGrant[]>>('/api/access/grants').then((r) => r.data.data ?? [])

export const grantAccess = (oid: string): Promise<void> =>
  api.post('/api/access/grants', { oid }).then(() => undefined)

export const revokeAccess = (userId: string): Promise<void> =>
  api.delete(`/api/access/grants/${encodeURIComponent(userId)}`).then(() => undefined)

export const getTenantAssessments = (tenantId: string): Promise<AssessmentRun[]> =>
  api.get<Envelope<RawRun[]>>(`/api/assessments/${tenantId}`).then((r) => (r.data.data ?? []).map(adaptRun))

export const getAssessmentRun = (runId: string): Promise<{ run: AssessmentRun; findings: Finding[]; governance: GovernanceScore }> =>
  api.get<Envelope<{ run: RawRun; findings: RawFinding[]; governance?: GovernanceScore }>>(`/api/assessments/run/${runId}`).then((r) => ({
    run: adaptRun(r.data.data.run),
    findings: (r.data.data.findings ?? []).map(adaptFinding),
    governance: r.data.data.governance ?? { total: 0, attested: 0, pct: 0 },
  }))

// ── Attestation evidence (governance controls; portal-local, no tenant writes) ─
interface RawAttestation {
  id: string; tenant_id: string; control_ref: string; owner: string | null; note: string | null
  evidence_ref: string | null; signed_off_by: string | null; signed_off_at: string | null; expires_at: string | null
}
const adaptAttestation = (a: RawAttestation): Attestation => ({
  id: a.id, tenantId: a.tenant_id, controlRef: a.control_ref, owner: a.owner, note: a.note,
  evidenceRef: a.evidence_ref, signedOffBy: a.signed_off_by, signedOffAt: a.signed_off_at, expiresAt: a.expires_at,
})
export const getAttestations = (tenantId: string): Promise<Attestation[]> =>
  api.get<Envelope<RawAttestation[]>>(`/api/attestations/${tenantId}`).then((r) => (r.data.data ?? []).map(adaptAttestation))
export const recordAttestation = (
  tenantId: string,
  // reviewIntervalDays (AIG-042): sets the review date when expiresAt is not given.
  body: { controlRef: string; note?: string; evidenceRef?: string; expiresAt?: string; reviewIntervalDays?: number }
): Promise<Attestation> =>
  api.post<Envelope<RawAttestation>>(`/api/attestations/${tenantId}`, body).then((r) => adaptAttestation(r.data.data))
export const revokeAttestation = (tenantId: string, controlRef: string): Promise<void> =>
  api.delete(`/api/attestations/${tenantId}/${encodeURIComponent(controlRef)}`).then(() => undefined)

/** One entry of the append-only attestation history (AIG-042): every record and revoke. */
export interface AttestationHistoryEntry {
  id: string
  controlRef: string
  action: 'record' | 'revoke'
  note: string | null
  signedOffBy: string | null
  signedOffAt: string | null
  expiresAt: string | null
  reviewIntervalDays: number | null
  recordedAt: string
}
interface RawAttestationHistory {
  id: string; control_ref: string; action: 'record' | 'revoke'; note: string | null; signed_off_by: string | null
  signed_off_at: string | null; expires_at: string | null; review_interval_days: number | null; recorded_at: string
}
export const getAttestationHistory = (tenantId: string, controlRef: string): Promise<AttestationHistoryEntry[]> =>
  api.get<Envelope<RawAttestationHistory[]>>(`/api/attestations/${tenantId}/history`, { params: { controlRef } })
    .then((r) => (r.data.data ?? []).map((h) => ({
      id: h.id, controlRef: h.control_ref, action: h.action, note: h.note, signedOffBy: h.signed_off_by,
      signedOffAt: h.signed_off_at, expiresAt: h.expires_at, reviewIntervalDays: h.review_interval_days, recordedAt: h.recorded_at,
    })))

async function downloadBlob(path: string, filename: string): Promise<void> {
  const r = await api.get(path, { responseType: 'blob' })
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ── E1: AI advisory narrative ──────────────────────────────────────────────
export interface AdvisoryNarrative {
  provider: string
  model: string
  createdAt: string
  execSummary: string
  postureRating: string
  keyRisks: string[]
  roadmap: { d30: string[]; d60: string[]; d90: string[] }
}

export const getAssessmentNarrative = (
  runId: string
): Promise<{ configured: boolean; narrative: AdvisoryNarrative | null }> =>
  api
    .get<Envelope<{ configured: boolean; narrative: AdvisoryNarrative | null }>>(
      `/api/assessments/run/${runId}/narrative`
    )
    .then((r) => r.data.data)

export const generateAssessmentNarrative = (runId: string): Promise<AdvisoryNarrative> =>
  api
    .post<Envelope<AdvisoryNarrative>>(`/api/assessments/run/${runId}/narrative`)
    .then((r) => r.data.data)

export const exportAssessmentPdf = (runId: string, filename: string): Promise<void> =>
  downloadBlob(`/api/assessments/run/${runId}/export/pdf`, filename)

export const exportAssessmentXlsx = (runId: string, filename: string): Promise<void> =>
  downloadBlob(`/api/assessments/run/${runId}/export/xlsx`, filename)

// ── E3: Discover / Shadow-IT ───────────────────────────────────────────────
export interface DiscoverSummaryEntry {
  tenantId: string
  tenantName: string
  totalApps: number
  thirdPartyApps: number
  orgWideConsents: number
  unverifiedThirdParty: number
  riskyApps: number
  lastSeen: string | null
}

export interface DiscoverApp {
  appId: string
  spObjectId: string
  displayName: string
  publisher: string | null
  publisherVerified: boolean
  appOwnerOrgId: string | null
  spType: string | null
  firstParty: boolean
  thirdParty: boolean
  accountEnabled: boolean
  delegatedScopes: string[]
  consentType: string | null
  riskScore: number
  riskBand: 'Critical' | 'High' | 'Medium' | 'Low'
  riskFactors: string[]
  lastSignIn: string | null
  firstSeen: string
  lastSeen: string
}

export const getDiscoverSummary = (): Promise<DiscoverSummaryEntry[]> =>
  api.get<Envelope<DiscoverSummaryEntry[]>>('/api/discover/summary').then((r) => r.data.data ?? [])

export const getTenantApps = (tenantId: string): Promise<{ tenantName: string; apps: DiscoverApp[]; collectedAt: string | null }> =>
  api
    .get<Envelope<{ tenantName: string; apps: DiscoverApp[]; collectedAt: string | null }>>(`/api/discover/${tenantId}/apps`)
    .then((r) => r.data.data)

export const refreshDiscovery = (tenantId: string): Promise<{ tenantName: string; apps: DiscoverApp[]; collectedAt: string | null }> =>
  api
    .post<Envelope<{ tenantName: string; apps: DiscoverApp[]; collectedAt: string | null }>>(`/api/discover/${tenantId}/refresh`)
    .then((r) => r.data.data)

export interface AppSignInUser { userId: string; upn: string | null; displayName: string | null; lastSignIn: string; count: number }
export const getAppUsers = (tenantId: string, appId: string): Promise<{ users: AppSignInUser[]; unavailable: boolean }> =>
  api.get<Envelope<{ users: AppSignInUser[]; unavailable: boolean }>>(`/api/discover/${tenantId}/apps/${appId}/users`).then((r) => r.data.data)

// ── Licensing (utilization + cost + waste + expiry + AI advisory) ──────────
// The backend snapshot is already camelCase, so no snake→camel adapter needed.
export interface LicensingSummaryEntry {
  tenantId: string
  tenantName: string
  skuCount: number
  totalSeats: number
  unusedSeats: number
  monthlyCostUsd: number | null
  wastedMonthlyCostUsd: number | null
  findingCount: number
  fetchedAt: string | null
}

export interface LicenseLine {
  skuId: string | null
  sku: string
  name: string
  used: number
  total: number
  suspended: number
  warning: number
  unused: number
  utilizationPct: number | null
  unitPriceUsd: number | null
  priceSource: 'contract' | 'distributor' | 'list' | null
  monthlyCostUsd: number | null
  unusedCostUsd: number | null
  isFreeOrTrial: boolean
  isConsumption: boolean
}

export type LicensingSeverity = 'info' | 'low' | 'medium' | 'high'

export interface LicensingFinding {
  code: 'unused_seats' | 'suspended_seats' | 'warning_state' | 'overlapping_sku' | 'trial' | 'expiring' | 'expired' | 'workload_underused'
  severity: LicensingSeverity
  sku: string | null
  title: string
  detail: string
  monthlyImpactUsd: number | null
}

export interface LicenseSubscriptionLine {
  sku: string | null
  name: string
  status: string | null
  isTrial: boolean
  isFreeOrTrial: boolean
  totalLicenses: number | null
  renewsOrExpires: string | null
  daysRemaining: number | null
}

export interface LicensingTotals {
  skuCount: number
  freeTrialSkuCount: number
  totalSeats: number
  assignedSeats: number
  unusedSeats: number
  suspendedSeats: number
  utilizationPct: number | null
  monthlyCostUsd: number | null
  wastedMonthlyCostUsd: number | null
  pricedCoverage: number
}

export interface WorkloadUsage {
  workload: string
  licensed: number
  active: number
  inactive: number
  inactivePct: number | null
}

export interface LicensingSnapshot {
  tenantId: string
  tenantName: string
  currency: 'USD'
  lines: LicenseLine[]
  subscriptions: LicenseSubscriptionLine[]
  workloads: WorkloadUsage[]
  totals: LicensingTotals
  findings: LicensingFinding[]
  fetchedAt: string | null
  subscriptionsAvailable: boolean
}

export interface LicensingAdvisory {
  tenantId: string
  tenantName: string
  provider: string
  model: string
  result: {
    execSummary: string
    costRating: string
    keyOpportunities: string[]
    recommendations: Array<{ title: string; rationale: string; estimatedMonthlySavingsUsd: number | null; effort: string }>
    bestPractices: string[]
  }
}

export const getLicensingSummary = (): Promise<LicensingSummaryEntry[]> =>
  api.get<Envelope<LicensingSummaryEntry[]>>('/api/licensing/summary').then((r) => r.data.data ?? [])

export const getLicensing = (tenantId: string): Promise<LicensingSnapshot> =>
  api.get<Envelope<LicensingSnapshot>>(`/api/licensing/${tenantId}`).then((r) => r.data.data)

export interface LicensingHistoryPoint {
  fetchedAt: string
  monthlyCostUsd: number | null
  wastedMonthlyCostUsd: number | null
  totalSeats: number
  unusedSeats: number
  skuCount: number
}
export const getLicensingHistory = (tenantId: string): Promise<LicensingHistoryPoint[]> =>
  api.get<Envelope<{ history: LicensingHistoryPoint[] }>>(`/api/licensing/${tenantId}/history`).then((r) => r.data.data.history ?? [])

export const refreshLicensing = (tenantId: string): Promise<LicensingSnapshot> =>
  api.post<Envelope<LicensingSnapshot>>(`/api/licensing/${tenantId}/refresh`).then((r) => r.data.data)

export const generateLicensingAdvisory = (tenantId: string): Promise<LicensingAdvisory> =>
  api.post<Envelope<LicensingAdvisory>>(`/api/licensing/${tenantId}/advisory`).then((r) => r.data.data)

export interface SkuOverrides { prices: Record<string, number>; free: Record<string, boolean> }
export const getLicenseOverrides = (tenantId: string): Promise<SkuOverrides> =>
  api.get<Envelope<{ prices?: Record<string, number>; free?: Record<string, boolean> }>>(`/api/licensing/${tenantId}/prices`)
    .then((r) => ({ prices: r.data.data.prices ?? {}, free: r.data.data.free ?? {} }))
export const setLicenseOverrides = (tenantId: string, o: SkuOverrides): Promise<LicensingSnapshot | { saved: boolean; refreshed: boolean }> =>
  api.put<Envelope<LicensingSnapshot | { saved: boolean; refreshed: boolean }>>(`/api/licensing/${tenantId}/prices`, o).then((r) => r.data.data)

// ── E4: Defender monitoring ────────────────────────────────────────────────
export interface DefenderSummaryEntry {
  tenantId: string
  tenantName: string
  alertCount: number
  alertActive: number
  alertHigh: number
  incidentCount: number
  incidentActive: number
  fetchedAt: string | null
}
export interface AlertUser {
  upn: string | null; displayName: string | null; accountName: string | null
  domain: string | null; sid: string | null; aadUserId: string | null; roles: string[]
}
export interface AlertIp { address: string; location: string | null }
export interface AlertDevice { name: string | null; osPlatform: string | null; healthStatus: string | null; riskScore: string | null }
export interface AlertMailbox { address: string | null; displayName: string | null }
export interface AlertFile { name: string | null; sha256: string | null; path: string | null }
export interface AlertApp { displayName: string | null; appId: string | null; publisher: string | null }
export interface DefenderAlert {
  id: string; title: string; description: string | null; severity: string; status: string
  category: string | null; serviceSource: string | null; detectionSource: string | null
  createdDateTime: string | null; firstActivityDateTime: string | null; lastActivityDateTime: string | null
  lastUpdateDateTime: string | null; incidentId: string | null; alertWebUrl: string | null
  assignedTo: string | null; determination: string | null; recommendedActions: string | null
  mitreTechniques: string[]
  users: AlertUser[]; ips: AlertIp[]; devices: AlertDevice[]; mailboxes: AlertMailbox[]
  files: AlertFile[]; urls: string[]; apps: AlertApp[]
}
export interface IncidentComment { comment: string; createdBy: string | null; createdDateTime: string | null }
export interface IncidentImpact { users: number; devices: number; mailboxes: number; apps: number; ips: number; countries: number }
export interface IncidentPriority {
  score: number
  level: 'critical' | 'high' | 'medium' | 'low'
  flags: Array<{ code: string; reason: string; severity: string }>
}
export interface DefenderIncident {
  id: string; displayName: string; severity: string; status: string
  classification: string | null; determination: string | null; createdDateTime: string | null
  lastUpdateDateTime: string | null; incidentWebUrl: string | null; assignedTo: string | null
  tags?: string[]; alertCount: number; relatedAlertIds: string[]; users: AlertUser[]
  // THR-5 enrichment (optional; absent on pre-THR-5 snapshots)
  description?: string | null; summary?: string | null
  comments?: IncidentComment[]; redirectIncidentId?: string | null; resolvingComment?: string | null
  detectionSources?: string[]; mitreTechniques?: string[]; impact?: IncidentImpact
  firstActivity?: string | null; lastActivity?: string | null
  severityCounts?: { high: number; medium: number; low: number; informational: number }
  priority?: IncidentPriority
}
export interface DefenderData {
  tenantName: string
  alerts: DefenderAlert[]
  incidents: DefenderIncident[]
  fetchedAt: string | null
  notConsented?: boolean
}

export const getDefenderSummary = (): Promise<DefenderSummaryEntry[]> =>
  api.get<Envelope<DefenderSummaryEntry[]>>('/api/defender/summary').then((r) => r.data.data ?? [])

export const getTenantDefender = (tenantId: string): Promise<DefenderData> =>
  api.get<Envelope<DefenderData>>(`/api/defender/${tenantId}`).then((r) => r.data.data)

// A live refresh fans out to several sequential Graph calls (alerts +
// incidents + evidence) and can legitimately run well past the global 30s
// client timeout on large tenants — allow up to 2 minutes for this call only.
export const refreshTenantDefender = (tenantId: string): Promise<DefenderData> =>
  api
    .post<Envelope<DefenderData>>(`/api/defender/${tenantId}/refresh`, undefined, { timeout: 120000 })
    .then((r) => r.data.data)

// ── THR: Unified Threats hub (supersedes Defender Alerts) ───────────────────────
export type ThreatCategory = 'identity' | 'email' | 'endpoint' | 'cloud_app' | 'oauth_app' | 'data' | 'config' | 'other'
export interface ThreatRiskFlag { code: string; reason: string; severity: string }
export interface ThreatEntities {
  users: AlertUser[]; ips: AlertIp[]; devices: AlertDevice[]; mailboxes: AlertMailbox[]
  files: AlertFile[]; urls: string[]; apps: AlertApp[]
}
export interface ThreatMdti { verdicts: Array<{ indicator: string; type: string; verdict: string; firstSeen: string | null }> }
export interface Threat {
  id: string; tenantId: string; source: string; sourceProduct: string | null; category: ThreatCategory
  title: string; description: string | null; severity: string; status: string; determination: string | null
  firstSeen: string | null; lastSeen: string | null; detectedAt: string | null
  incidentRef: string | null; correlationKey: string | null; mitreTechniques: string[]
  entities: ThreatEntities; riskFlags: ThreatRiskFlag[]; recommendedActions: string | null; deepLink: string | null
  mdti?: ThreatMdti
}
export interface ThreatSourceState {
  key: string; label: string; enabled: boolean; consented: boolean; licensed: boolean
  unavailableReason: string | null; lastError: string | null; fetchedAt: string | null
}
export interface ThreatSummaryShape {
  total: number; active: number; high: number; incidentsTotal: number; incidentsActive: number
  byCategory: Record<string, number>; new24h: number; new7d: number
}
export interface ThreatSnapshot {
  tenantId: string; tenantName?: string; fetchedAt: string | null
  threats: Threat[]; incidents: DefenderIncident[]; sources: ThreatSourceState[]; summary: ThreatSummaryShape
}
export interface ThreatSummaryEntry {
  tenantId: string; tenantName: string; total: number; active: number; high: number
  incidentActive: number; fetchedAt: string | null
}
export interface ThreatRefreshStatus {
  tenantId: string; running: boolean; last: { at: string; error: string | null } | null
}

export const getThreatSummary = (): Promise<ThreatSummaryEntry[]> =>
  api.get<Envelope<ThreatSummaryEntry[]>>('/api/threats/summary').then((r) => r.data.data ?? [])

export const getTenantThreats = (tenantId: string): Promise<ThreatSnapshot> =>
  api.get<Envelope<ThreatSnapshot>>(`/api/threats/${tenantId}`).then((r) => r.data.data)

// Background refresh: 202 + poll getThreatRefreshStatus until !running (mirrors MFA/PIM).
export const refreshTenantThreats = (
  tenantId: string
): Promise<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }> =>
  api
    .post<Envelope<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }>>(
      `/api/threats/${tenantId}/refresh`
    )
    .then((r) => r.data.data)

export const getThreatRefreshStatus = (tenantId: string): Promise<ThreatRefreshStatus> =>
  api.get<Envelope<ThreatRefreshStatus>>(`/api/threats/${tenantId}/refresh/status`).then((r) => r.data.data)

// ── E12: Devices & endpoint estate ─────────────────────────────────────────────
// The backend already emits camelCase for this domain (deviceNormalize builds the
// objects directly), so no snake_case adaptation is needed here.

export type DeviceComplianceState =
  | 'compliant' | 'noncompliant' | 'inGracePeriod' | 'unknown' | 'conflict' | 'error' | 'notApplicable'
export type DeviceSourceKey = 'intune' | 'mde' | 'entra'
/** Entra join type. 'unknown' is a real state, never folded into 'registered'. */
export type DeviceJoinType = 'entraJoined' | 'hybridJoined' | 'registered' | 'unknown'

/** Per-source coverage. `unavailableReason` distinguishes "not consented" from
 *  "not licensed" from a transient fault — three different operator actions, so
 *  the hub must never collapse them into one empty table. */
export interface DeviceSourceState {
  key: DeviceSourceKey
  label: string
  enabled: boolean
  consented: boolean
  licensed: boolean
  unavailableReason: string | null
  lastError: string | null
  fetchedAt: string | null
}

export interface ManagedDevice {
  id: string
  deviceName: string
  intuneId: string | null
  mdeDeviceId: string | null
  azureAdDeviceId: string | null
  complianceState: DeviceComplianceState
  osPlatform: string
  osVersion: string | null
  lastSync: string | null
  /** null = genuinely unknown. NEVER render null as "unencrypted". */
  isEncrypted: boolean | null
  ownership: 'company' | 'personal' | 'unknown'
  // Identity + lifecycle (Entra)
  entraObjectId: string | null
  joinType: DeviceJoinType
  registeredAt: string | null
  /** Approximate last interactive sign-in — NOT the Intune check-in (`lastSync`). */
  lastSignIn: string | null
  accountEnabled: boolean | null
  manufacturer: string | null
  model: string | null

  /** Intune primary user. null = no assignment reported — a shared or kiosk device,
   *  or a row only Entra/MDE knows about. Never render null as a person. */
  primaryUserUpn: string | null
  primaryUserName: string | null
  primaryUserId: string | null

  mdeOnboarded: boolean | null
  exposureLevel: string | null
  riskScore: string | null
  vulnerabilities: { critical: number; high: number; medium: number; low: number } | null
  sources: DeviceSourceKey[]
}

export interface DeviceSummaryCounts {
  total: number
  compliant: number
  noncompliant: number
  inGracePeriod: number
  complianceUnknown: number
  /** The AB-DV-07-aligned 30-day figure. Identical to staleOver30. */
  stale: number
  /** Cumulative subsets: staleOver30 ⊇ staleOver60 ⊇ staleOver90. */
  staleOver30: number
  staleOver60: number
  staleOver90: number
  unencrypted: number
  atRisk: number
  notMdeOnboarded: number
  personallyOwned: number
  byPlatform: Record<string, number>
}

/** Tenant-level CVE roll-up. machineCount is DISTINCT machines, not rows. */
export interface CveSummary {
  cveId: string
  severity: string
  machineCount: number
  productName: string | null
}

export interface SecureConfigFinding {
  configurationId: string
  configurationName: string
  category: string | null
  deviceCount: number
}

export interface DeviceSnapshot {
  tenantId: string
  tenantName?: string
  fetchedAt: string | null
  devices: ManagedDevice[]
  compliancePolicies: unknown[]
  configProfiles: unknown[]
  sources: DeviceSourceState[]
  summary: DeviceSummaryCounts
  /** [] means "looked and found none". Distinguish from an unavailable source
   *  via `sources` — an empty list is not the same as no visibility. */
  topCves: CveSummary[]
  secureConfigFindings: SecureConfigFinding[]
}

export interface DeviceSummaryEntry {
  tenantId: string
  tenantName: string
  total: number
  compliant: number
  noncompliant: number
  stale: number
  staleOver60: number
  staleOver90: number
  atRisk: number
  fetchedAt: string | null
}

export interface DeviceRefreshStatus {
  tenantId: string
  running: boolean
  last: { at: string; error: string | null } | null
}

/** Optional server-side filters. Backend validates with Zod `.strict()`, so an
 *  unrecognised key is a 400 rather than being silently ignored. */
export interface DeviceFilters {
  stale?: '30' | '60' | '90'
  compliance?: 'compliant' | 'noncompliant' | 'inGracePeriod' | 'unknown'
  source?: DeviceSourceKey
  encryption?: 'encrypted' | 'unencrypted' | 'unknown'
}

export const getDeviceSummary = (): Promise<DeviceSummaryEntry[]> =>
  api.get<Envelope<DeviceSummaryEntry[]>>('/api/devices/summary').then((r) => r.data.data ?? [])

export const getTenantDevices = (tenantId: string, filters: DeviceFilters = {}): Promise<DeviceSnapshot> =>
  api
    .get<Envelope<DeviceSnapshot>>(`/api/devices/${tenantId}`, { params: filters })
    .then((r) => r.data.data)

// Background refresh: 202 + poll getDeviceRefreshStatus until !running.
export const refreshTenantDevices = (
  tenantId: string
): Promise<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }> =>
  api
    .post<Envelope<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }>>(
      `/api/devices/${tenantId}/refresh`
    )
    .then((r) => r.data.data)

export const getDeviceRefreshStatus = (tenantId: string): Promise<DeviceRefreshStatus> =>
  api.get<Envelope<DeviceRefreshStatus>>(`/api/devices/${tenantId}/refresh/status`).then((r) => r.data.data)

// ── CM: M365 Configuration Change Monitor ──────────────────────────────────────
// The backend already returns camelCase (mapChangeEventRow), so no adaptation.
export type ChangeRiskLevel = 'low' | 'medium' | 'high' | 'critical'
export type ChangeDirection = 'hardening' | 'weakening' | 'neutral'
export type ChangeType = 'create' | 'update' | 'delete' | 'enable' | 'disable'
export type ChangeReviewStatus = 'new' | 'reviewed' | 'escalated' | 'dismissed'

export interface ChangeEvent {
  id: string
  tenantId: string
  ts: string
  workload: string
  operationName: string | null
  operationTechnical: string | null
  changedObjectType: string | null
  changedObjectId: string | null
  changedObjectName: string | null
  actorUpn: string | null
  actorId: string | null
  actorSpId: string | null
  actorDisplayName: string | null
  clientApp: string | null
  origin: string | null
  changeType: ChangeType | null
  before: unknown
  after: unknown
  riskLevel: ChangeRiskLevel
  riskScore: number
  riskReasons: string[] | null
  changeDirection: ChangeDirection
  changeSource: string
  baselineDeviation: boolean
  baselineId: string | null
  offHours: boolean
  approvalReference: string | null
  reviewStatus: ChangeReviewStatus
  reviewedBy: string | null
  reviewedAt: string | null
  notes: string | null
  rawId: string | null
  fetchedAt: string
}

export interface ChangeEventDetail extends ChangeEvent {
  raw: unknown
  objectHistory: ChangeEvent[]
}

export interface ChangesSummary {
  total: number
  byWorkload: { workload: string; count: number }[]
  byRisk: { critical: number; high: number; medium: number; low: number }
  byDirection: { weakening: number; hardening: number; neutral: number }
  weakening: number
  offHours: number
  baselineDeviation: number
  unreviewed: number
  topObjects: { changedObjectId: string; changedObjectName: string | null; count: number }[]
  trend: { date: string; count: number }[]
  /** When audit content was last pulled for this tenant. null in fleet scope — one
   *  timestamp cannot honestly stand for many tenants. */
  collectedAt: string | null
  /** How often the server pulls new audit content, in minutes; null when the scheduler
   *  is off. The page states this instead of a hard-coded cadence (AB#113526). */
  ingestEveryMinutes?: number | null
}

export interface ChangeListParams {
  tenantId?: string
  workload?: string
  /** One level, or 'critical,high' for high and above (AB#113527). */
  riskLevel?: ChangeRiskLevel | 'critical,high'
  actorId?: string
  objectId?: string
  changeType?: ChangeType
  changeDirection?: ChangeDirection
  reviewStatus?: ChangeReviewStatus
  baselineDeviation?: boolean
  offHours?: boolean
  from?: string
  to?: string
  q?: string
  limit?: number
  offset?: number
}

export interface ChangeListResult {
  rows: ChangeEvent[]
  total: number
  limit: number
  offset: number
}

/** Omit empty / undefined keys so they never serialise as `severity=undefined`. */
function sparseQuery(params: object): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

function changeQuery(params: ChangeListParams): string {
  return sparseQuery(params)
}

export const getChangesSummary = (params: { tenantId?: string; from?: string } = {}): Promise<ChangesSummary> =>
  api.get<Envelope<ChangesSummary>>(`/api/changes/summary${changeQuery(params)}`).then((r) => r.data.data)

export const getChanges = (params: ChangeListParams = {}): Promise<ChangeListResult> =>
  api.get<Envelope<ChangeEvent[]>>(`/api/changes${changeQuery(params)}`).then((r) => ({
    rows: r.data.data ?? [],
    total: Number(r.data.meta?.['total'] ?? 0),
    limit: Number(r.data.meta?.['limit'] ?? 50),
    offset: Number(r.data.meta?.['offset'] ?? 0),
  }))

export const getChange = (id: string): Promise<ChangeEventDetail> =>
  api.get<Envelope<ChangeEventDetail>>(`/api/changes/${id}`).then((r) => r.data.data)

export const reviewChange = (
  id: string,
  body: { reviewStatus?: ChangeReviewStatus; notes?: string }
): Promise<ChangeEvent> =>
  api.post<Envelope<ChangeEvent>>(`/api/changes/${id}/review`, body).then((r) => r.data.data)

// not_licensed (AB#112264): per source only, the Intune source on a tenant with no Intune plan.
export type IngestHealth = 'ok' | 'not_consented' | 'audit_not_enabled' | 'not_licensed' | 'error'
export interface ChangesRefreshOutcome {
  at: string
  rawStored: number
  eventsStored: number
  errors: number
  /** First-class outcome of the last attempt — drives the prereq banner. */
  health?: IngestHealth
  detail?: string | null
  /** Which kind of pull produced this outcome (AB#113525). */
  mode?: 'refresh' | 'replay'
}
export interface ChangesRefreshStarted {
  tenantId: string
  status: 'started' | 'already_running'
  last: ChangesRefreshOutcome | null
}
export interface ChangesRefreshStatus {
  tenantId: string
  running: boolean
  last: ChangesRefreshOutcome | null
}

// The ingest runs in the BACKGROUND (a first pull can take minutes), so this POST
// returns 202 immediately — no long client timeout. Poll refreshStatus for results.
export const refreshChanges = (tenantId: string): Promise<ChangesRefreshStarted> =>
  api.post<Envelope<ChangesRefreshStarted>>(`/api/changes/${tenantId}/refresh`).then((r) => r.data.data)

export const getChangesRefreshStatus = (tenantId: string): Promise<ChangesRefreshStatus> =>
  api.get<Envelope<ChangesRefreshStatus>>(`/api/changes/${tenantId}/refresh/status`).then((r) => r.data.data)

export type ChangesHealthStatus = IngestHealth
export interface ChangesHealthCounts {
  blobs: number
  records: number
  matched: number
  stored: number
  duplicates: number
  dropped: number
  droppedOps: string[]
}
export interface ChangesHealthContentType {
  contentType: string
  lastBlobTime: string | null
  lastRecordId: string | null
  lastRunAt: string | null
  lastSuccessAt: string | null
  lastStatus: ChangesHealthStatus | null
  lastError: string | null
  counts: ChangesHealthCounts | null
  updatedAt: string
}
export interface ChangesHealth {
  tenantId: string
  overall: {
    status: ChangesHealthStatus
    lastRunAt: string | null
    lastSuccessAt: string | null
    message: string | null
  }
  byContentType: ChangesHealthContentType[]
}

export const getChangesHealth = (tenantId: string): Promise<ChangesHealth> =>
  api.get<Envelope<ChangesHealth>>(`/api/changes/health${sparseQuery({ tenantId })}`).then((r) => r.data.data)

// AB#113525: re-fetch and reprocess the last 1-7 days for one tenant (SuperAdmin).
// Same background 202 as refresh and the same per-tenant guard, so poll
// getChangesRefreshStatus for the outcome (its `last.mode` is 'replay').
export interface ChangesReplayStarted {
  tenantId: string
  status: 'started' | 'already_running'
  mode: 'replay'
  days: number
  last: ChangesRefreshOutcome | null
}
export const replayChanges = (tenantId: string, days: number): Promise<ChangesReplayStarted> =>
  api.post<Envelope<ChangesReplayStarted>>(`/api/changes/${tenantId}/replay`, { days }).then((r) => r.data.data)

export type ChangeAlertScope = 'msp' | 'org'
export type ChangeAlertMinRisk = 'critical' | 'high' | 'medium'
export type ChangeAlertMode = 'immediate' | 'daily' | 'weekly'
export interface ChangeAlertSubscription {
  id: string
  scope: ChangeAlertScope
  orgId: string | null
  channel: 'email' | 'teams'
  email: string | null
  webhookSecretName: string | null
  minRisk: ChangeAlertMinRisk
  mode: ChangeAlertMode
  enabled: boolean
  createdBy: string | null
  createdAt: string
  /** End of the last digest period sent (daily / weekly only); null until the first one. */
  lastDigestAt: string | null
  /** Application module the subscription watches (backend services/alertModules.ts). */
  module: string
}
export interface ChangeAlertSubscriptionInput {
  scope: ChangeAlertScope
  orgId?: string | null
  channel: 'email' | 'teams'
  email?: string | null
  webhookSecretName?: string | null
  minRisk: ChangeAlertMinRisk
  mode: ChangeAlertMode
  enabled?: boolean
  /** Omitted = Change Monitor. */
  module?: string
}

/** One sidebar module in the Alert settings dropdown, and what alerting exists for it. */
export interface AlertModuleInfo {
  key: string
  label: string
  group: string
  path: string
  /** False = listed so the whole application is visible, but no alerts exist for it yet. */
  supported: boolean
  trigger: string | null
  modes: ChangeAlertMode[]
  minRisks: ChangeAlertMinRisk[]
}

export const listAlertModules = (): Promise<AlertModuleInfo[]> =>
  api.get<Envelope<AlertModuleInfo[]>>('/api/changes/alerts/modules').then((r) => r.data.data ?? [])

export const listChangeAlertSubscriptions = (): Promise<ChangeAlertSubscription[]> =>
  api.get<Envelope<ChangeAlertSubscription[]>>('/api/changes/alerts/subscriptions').then((r) => r.data.data ?? [])

export const createChangeAlertSubscription = (body: ChangeAlertSubscriptionInput): Promise<ChangeAlertSubscription> =>
  api.post<Envelope<ChangeAlertSubscription>>('/api/changes/alerts/subscriptions', body).then((r) => r.data.data)

export const updateChangeAlertSubscription = (
  id: string,
  body: Partial<Pick<ChangeAlertSubscription, 'email' | 'minRisk' | 'mode' | 'enabled' | 'webhookSecretName'>>
): Promise<ChangeAlertSubscription> =>
  api.patch<Envelope<ChangeAlertSubscription>>(`/api/changes/alerts/subscriptions/${id}`, body).then((r) => r.data.data)

export const deleteChangeAlertSubscription = (id: string): Promise<void> =>
  api.delete(`/api/changes/alerts/subscriptions/${id}`).then(() => undefined)

// ── Tenant posture + license seats (stories 04/05) ─────────────────────────
export interface LicenseSeat { sku: string; name: string; used: number; total: number }
export interface TenantPosture {
  postureType: string
  postureDetail: string | null
  caEnabled: number
  caReportOnly: number
  securityDefaults: boolean | null
  licenses: LicenseSeat[]
  fetchedAt: string | null
}

export const getTenantPosture = (id: string): Promise<TenantPosture> =>
  api.get<Envelope<TenantPosture>>(`/api/tenants/${id}/posture`).then((r) => r.data.data)

export const refreshTenantPosture = (id: string): Promise<TenantPosture> =>
  api.post<Envelope<TenantPosture>>(`/api/tenants/${id}/posture/refresh`).then((r) => r.data.data)

export const listBaselines = (tenantId?: string): Promise<Baseline[]> =>
  api.get<Envelope<RawBaseline[]>>(`/api/baselines${tenantId ? `?tenantId=${tenantId}` : ''}`).then((r) => (r.data.data ?? []).map(adaptBaseline))

export const deriveBaseline = (input: { frameworkId: string; name: string; tenantId?: string }): Promise<Baseline> =>
  api.post<Envelope<RawBaseline>>('/api/baselines/from-framework', input).then((r) => adaptBaseline(r.data.data))

export const getBaselineDetail = (id: string): Promise<{ baseline: Baseline; latestDrift: BaselineDrift | null }> =>
  api.get<Envelope<{ baseline: RawBaseline; latestDrift: RawDrift | null }>>(`/api/baselines/${id}`).then((r) => ({
    baseline: adaptBaseline(r.data.data.baseline),
    latestDrift: r.data.data.latestDrift ? adaptDrift(r.data.data.latestDrift) : null,
  }))

export const computeBaselineDrift = (baselineId: string, runId: string): Promise<BaselineDrift> =>
  api.post<Envelope<RawDrift>>(`/api/baselines/${baselineId}/drift`, { runId }).then((r) => adaptDrift(r.data.data))

// ─── Fleet threat correlation ─────────────────────────────────────────────────

interface RawFleetIndicator {
  id: string; type: FleetIndicatorType; value: string; severity: string
  label: string | null; description: string | null; source: string
  is_active: number; created_at: string
}
interface RawFleetMatch {
  id: string; indicator_id: string; tenant_id: string; status: string
  indicator_type: string; indicator_value: string; indicator_severity: string
  indicator_label: string | null; tenant_name: string; first_seen: string; last_seen: string
}

const adaptFleetIndicator = (i: RawFleetIndicator): FleetIndicator => ({
  id: i.id, type: i.type, value: i.value, severity: i.severity, label: i.label,
  description: i.description, source: i.source, isActive: i.is_active === 1, createdAt: i.created_at,
})
const adaptFleetMatch = (m: RawFleetMatch): FleetMatch => ({
  id: m.id, indicatorId: m.indicator_id, tenantId: m.tenant_id, status: m.status,
  indicatorType: m.indicator_type, indicatorValue: m.indicator_value, indicatorSeverity: m.indicator_severity,
  indicatorLabel: m.indicator_label, tenantName: m.tenant_name, firstSeen: m.first_seen, lastSeen: m.last_seen,
})

export const getFleetSummary = (): Promise<FleetSummary> =>
  api.get<Envelope<FleetSummary>>('/api/fleet/summary').then((r) => r.data.data)

export const getFleetIndicators = (): Promise<FleetIndicator[]> =>
  api.get<Envelope<RawFleetIndicator[]>>('/api/fleet/indicators').then((r) => (r.data.data ?? []).map(adaptFleetIndicator))

export const getFleetMatches = (): Promise<FleetMatch[]> =>
  api.get<Envelope<RawFleetMatch[]>>('/api/fleet/matches').then((r) => (r.data.data ?? []).map(adaptFleetMatch))

export const createFleetIndicator = (input: {
  type: FleetIndicatorType; value: string; severity?: string; label?: string
}): Promise<{ indicator: FleetIndicator; fanOut: { matches: number; newMatches: number } }> =>
  api.post<Envelope<{ indicator: RawFleetIndicator; fanOut: { matches: number; newMatches: number } }>>('/api/fleet/indicators', input)
    .then((r) => ({ indicator: adaptFleetIndicator(r.data.data.indicator), fanOut: r.data.data.fanOut }))

export const deleteFleetIndicator = (id: string): Promise<void> =>
  api.delete(`/api/fleet/indicators/${id}`).then(() => undefined)

export const setFleetMatchStatus = (id: string, status: 'open' | 'dismissed' | 'confirmed'): Promise<void> =>
  api.put(`/api/fleet/matches/${id}/status`, { status }).then(() => undefined)

export const runFleetSweep = (): Promise<FleetSweepResult> =>
  api.post<Envelope<FleetSweepResult>>('/api/fleet/sweep').then((r) => r.data.data)

// ─── Global search (112663) ───────────────────────────────────────────────────

/** Backend enforces a 2-char minimum; the caller should not fire below it. */
export const SEARCH_MIN_LEN = 2

export interface SearchHit {
  kind: 'tenant' | 'user' | 'finding'
  id: string
  label: string
  sublabel: string
  tenantId?: string
  isGlobalAdmin?: boolean
  severity?: string | null
  outcome?: string | null
}

export interface SearchResults {
  tenants: SearchHit[]
  users: SearchHit[]
  findings: SearchHit[]
  total: number
  truncated: boolean
}

interface RawSearchHit {
  kind: 'tenant' | 'user' | 'finding'
  id: string
  label: string
  sublabel: string
  tenant_id?: string
  is_global_admin?: boolean
  severity?: string | null
  outcome?: string | null
}

const adaptHit = (h: RawSearchHit): SearchHit => ({
  kind: h.kind,
  id: h.id,
  label: h.label,
  sublabel: h.sublabel,
  tenantId: h.tenant_id,
  isGlobalAdmin: h.is_global_admin,
  severity: h.severity,
  outcome: h.outcome,
})

export const globalSearch = (q: string, signal?: AbortSignal): Promise<SearchResults> =>
  api
    .get<Envelope<{ tenants: RawSearchHit[]; users: RawSearchHit[]; findings: RawSearchHit[] }>>('/api/search', {
      params: { q },
      signal,
    })
    .then((r) => {
      const d = r.data.data
      const meta = (r.data.meta ?? {}) as { total?: number; truncated?: boolean }
      return {
        tenants: (d?.tenants ?? []).map(adaptHit),
        users: (d?.users ?? []).map(adaptHit),
        findings: (d?.findings ?? []).map(adaptHit),
        total: meta.total ?? 0,
        truncated: !!meta.truncated,
      }
    })

// ── Conditional Access hub (AB #113034) ───────────────────────────────────────
// Types declared HERE rather than in types/index.ts: that file holds only the older
// adapted domains (Tenant, MFA, PIM, ScoreSnapshot). Every hub-style domain - Devices,
// Threats - keeps its types beside its helpers, and caPolicyService builds these as
// camelCase already, so there is no snake_case adapter for this domain.

export interface CaSourceState {
  key: string
  label: string
  consented: boolean
  unavailableReason: string | null
  lastError: string | null
  fetchedAt: string | null
  /** The read hit its page ceiling, so any count from it is a floor. */
  truncated?: boolean
}

export interface CaSummaryCounts {
  total: number
  enabled: number
  reportOnly: number
  disabled: number
  requireMfa: number
  blockPolicies: number
  namedLocations: number
  trustedLocations: number
  staleReferences: number
  critical: number
  high: number
  medium: number
  low: number
}

/** The narrowed Graph policy the view model reads structurally. Deliberately loose:
 *  the backend stores the raw object, and pinning every Graph field here would break
 *  the moment Microsoft adds one. */
export interface CaRawPolicy {
  id?: string | null
  displayName?: string | null
  state?: string | null
  createdDateTime?: string | null
  modifiedDateTime?: string | null
  conditions?: Record<string, unknown> | null
  grantControls?: Record<string, unknown> | null
  sessionControls?: Record<string, unknown> | null
}

export interface CaPolicyView {
  id: string | null
  displayName: string | null
  state: string | null
  createdDateTime: string | null
  modifiedDateTime: string | null
  /** Include/exclude targets resolved to names, so the UI never renders a bare GUID. */
  resolved: Array<{ id: string; name: string; type: string }>
  /** Referenced ids that no longer resolve in the directory. */
  unresolved: string[]
  raw: CaRawPolicy
}

export interface CaNamedLocation {
  id?: string | null
  displayName?: string | null
  isTrusted?: boolean | null
}

export interface CaFinding {
  id: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  area: string
  title: string
  detail: string
  affectedPolicies: string[]
}

export interface CaSnapshot {
  tenantId: string
  tenantName?: string
  /** null = never collected. The ONLY signal for that; the API never 404s instead. */
  fetchedAt: string | null
  policies: CaPolicyView[]
  namedLocations: CaNamedLocation[]
  findings: CaFinding[]
  sources: CaSourceState[]
  summary: CaSummaryCounts
}

export interface CaTenantSummary {
  tenantId: string
  tenantName: string
  fetchedAt: string | null
  total: number
  enabled: number
  reportOnly: number
  disabled: number
  staleRefs: number
  high: number
  medium: number
  low: number
}

export interface CaRefreshStatus {
  tenantId: string
  running: boolean
  last: { at: string; error: string | null } | null
}

/** EXACTLY the three keys backend/src/routes/conditionalAccess.ts accepts. That schema is
 *  `.strict()`, so an extra or misspelled key is a 400 `invalid_query`, not a silent
 *  full-set return - which is the point. Keep this in step with it. */
export interface CaFilters {
  state?: 'enabled' | 'reportOnly' | 'disabled'
  severity?: 'critical' | 'high' | 'medium' | 'low'
  q?: string
}

// Named getConditionalAccessSummary, not getCaSummary: the backend service already exports
// a getCaSummary(tenantIds) and the collision reads as the same function across the wire.
export const getConditionalAccessSummary = (): Promise<CaTenantSummary[]> =>
  api.get<Envelope<CaTenantSummary[]>>('/api/conditional-access/summary').then((r) => r.data.data ?? [])

export const getTenantConditionalAccess = (
  tenantId: string,
  filters: CaFilters = {}
): Promise<CaSnapshot> =>
  api
    .get<Envelope<CaSnapshot>>(`/api/conditional-access/${tenantId}`, { params: filters })
    .then((r) => r.data.data)

// Background refresh: 202 + poll getCaRefreshStatus until !running.
export const refreshTenantConditionalAccess = (
  tenantId: string
): Promise<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }> =>
  api
    .post<Envelope<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }>>(
      `/api/conditional-access/${tenantId}/refresh`
    )
    .then((r) => r.data.data)

// ── App Credential Expiry hub (Identity) ─────────────────────────────────────
// Display-only view of client secrets, certificates and SAML signing certificates on app
// registrations and enterprise apps. appCredentialService builds these as camelCase, so
// there is no adapter. `daysLeft` and `status` are derived by the server against TODAY on
// every read, not frozen at collection time.

export type AppCredType = 'clientSecret' | 'certificate' | 'samlSigningCertificate'
/** Appendix C Status: OK, Expiring (within 30 days), Expired; unknown = unreadable date. */
export type AppCredStatus = 'expired' | 'expiring' | 'ok' | 'unknown'
/** §6 step 9: the smallest threshold reached (30, 7 or 0); null = no stage. */
export type AppCredStage = 30 | 7 | 0 | null

/** A USER owner (§6 step 7: /owners/microsoft.graph.user). */
export interface AppCredOwner {
  id: string
  displayName: string | null
  mail: string | null
  userPrincipalName: string | null
  /** Nullable: null = Graph did not say, NOT disabled. */
  accountEnabled: boolean | null
  /** §7: mail, else UPN; guest #EXT# UPNs skipped. Null = no resolvable address. */
  email: string | null
}

export interface AppCredential {
  id: string
  objectType: 'application' | 'servicePrincipal'
  objectId: string
  appId: string | null
  appName: string
  credentialType: AppCredType
  credentialName: string | null
  keyId: string | null
  startDateTime: string | null
  endDateTime: string | null
  owners: AppCredOwner[]
  /** False = the owner lookup failed. Render "not checked", never "no owner". */
  ownersChecked: boolean
  /** §7 Notify: contacts from the app's Internal notes. */
  notifyContacts: string[]
  /** §7: who a reminder goes to (owners + Notify:, domain-filtered). Empty = admin fallback. */
  recipients: string[]
  /** §7 Needs attention: in the 30-day window with no resolvable recipient. */
  needsAttention: boolean
  /** The last reminder recorded for this credential (state), or null if none yet. */
  reminder?: { stage: number; lastSent: string | null; suppressed: boolean } | null
  /** §6 step 8: calendar days in the run time zone; negative = expired. */
  daysLeft: number | null
  stage: AppCredStage
  status: AppCredStatus
  /** Expired more than 30 days ago (§9 DigestLookbackDays): a leftover. */
  longExpired: boolean
  /** Appendix C ExpiresLocal: `yyyy-MM-dd HH:mm` in the run time zone. */
  expiresLocal: string | null
}

export interface AppCredSummaryCounts {
  total: number
  expired: number
  expiring30: number
  expiring7: number
  longExpired: number
  /** §7 Needs attention: apps in the window with no reachable owner. */
  needsAttention: number
  ownersUnchecked: number
}

export interface AppCredSourceState {
  key: string
  label: string
  /** False only for a permission / consent gap. */
  consented: boolean
  unavailableReason: string | null
  lastError: string | null
  /** null = this read did not succeed. */
  fetchedAt: string | null
  truncated?: boolean
  /** The read failed and its rows are the last good data from this time. */
  carriedOverFrom?: string | null
}

export interface AppCredSnapshot {
  tenantId: string
  tenantName?: string
  /** Entra tenant (directory) ID — §2 traceability. */
  tenantDirectoryId?: string | null
  /** null = never collected. */
  fetchedAt: string | null
  /** IANA zone days left and expiresLocal use (§8; default Pacific). */
  timeZone: string
  credentials: AppCredential[]
  sources: AppCredSourceState[]
  summary: AppCredSummaryCounts
}

export interface AppCredTenantSummary extends AppCredSummaryCounts {
  tenantId: string
  tenantName: string
  fetchedAt: string | null
  /** A source failed or was truncated, so counts may be a floor. */
  partial: boolean
}

export interface AppCredRefreshStatus {
  tenantId: string
  running: boolean
  last: { at: string; error: string | null } | null
}

/** EXACTLY the keys backend/src/routes/appCredentials.ts accepts (a `.strict()` schema). */
export interface AppCredFilters {
  status?: AppCredStatus
  type?: AppCredType
  q?: string
}

export const getAppCredentialSummary = (): Promise<AppCredTenantSummary[]> =>
  api.get<Envelope<AppCredTenantSummary[]>>('/api/app-credentials/summary').then((r) => r.data.data ?? [])

export const getTenantAppCredentials = (tenantId: string, filters: AppCredFilters = {}): Promise<AppCredSnapshot> =>
  api
    .get<Envelope<AppCredSnapshot>>(`/api/app-credentials/${tenantId}`, { params: filters })
    .then((r) => r.data.data)

// Background refresh: 202 + poll getAppCredRefreshStatus until !running.
export const refreshTenantAppCredentials = (
  tenantId: string
): Promise<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }> =>
  api
    .post<Envelope<{ tenantId: string; status: string; last: { at: string; error: string | null } | null }>>(
      `/api/app-credentials/${tenantId}/refresh`
    )
    .then((r) => r.data.data)

export const getAppCredRefreshStatus = (tenantId: string): Promise<AppCredRefreshStatus> =>
  api.get<Envelope<AppCredRefreshStatus>>(`/api/app-credentials/${tenantId}/refresh/status`).then((r) => r.data.data)

// ── AI Governance (AIG-011): agent inventory. MSP-only; camelCase from the backend ──

export type AgentType = 'microsoft' | 'this_tenant' | 'third_party' | 'unknown'

export interface AiAgent {
  id: string
  displayName: string | null
  blueprintId: string | null
  type: AgentType
  publisherTenantId: string | null
  /** null = owners were NOT READ; [] = read, none. Never render null as "no owner". */
  owners: Array<{ id: string; displayName: string | null }> | null
  /** Sponsors (AIG-020). Same null/[] contract as owners; absent on snapshots stored before. */
  sponsors?: Array<{ id: string; displayName: string | null }> | null
  accountEnabled: boolean | null
  createdAt: string | null
}

export interface AiGovSourceState {
  key: string
  label: string
  consented: boolean
  unavailableReason: string | null
  lastError: string | null
  fetchedAt: string | null
  truncated?: boolean
  /** 'beta' = a Microsoft preview API; the page says so. */
  apiVersion: 'v1.0' | 'beta'
}

export interface AiAgentSummary {
  total: number
  microsoft: number
  thisTenant: number
  thirdParty: number
  unknownType: number
  noOwner: number
  /** Agents with neither an owner nor a sponsor, both read (AIG-020). */
  unaccountable: number
  disabled: number
}

/** A hub check (AIG-020/021). Internal-only: built on preview (beta) sources. */
export interface AiGovCheck {
  id: 'agent-accountability' | 'ca-covers-agents'
  title: string
  outcome: 'pass' | 'partial' | 'fail' | 'skipped'
  skipReason: string | null
  detail: string
  affected: string[]
}

export interface RiskyItem {
  id: string
  displayName: string | null
  kind: 'servicePrincipal' | 'agentIdentity' | 'agentUser' | 'agentIdentityBlueprintPrincipal' | 'unknown'
  riskLevel: string | null
  riskState: string | null
  riskDetail: string | null
  lastUpdated: string | null
  isEnabled: boolean | null
}

/** Risky service principals and risky agents (AIG-022). A null list = NOT READ, never "none at risk". */
export interface AgentRisk {
  fetchedAt: string
  riskyServicePrincipals: RiskyItem[] | null
  riskyAgents: RiskyItem[] | null
}

/** Copilot licences and active users (AIG-010). `footprint: 'none'` = no AI footprint, never compliance. */
export interface CopilotAdoption {
  footprint: 'licensed' | 'none' | 'unknown'
  skus: Array<{ skuPartNumber: string; skuId: string | null; purchased: number | null; assigned: number | null }>
  purchased: number
  assigned: number
  fetchedAt: string
  /** null = not read (no Copilot, or the usage report failed): never "0 active users". */
  activeUsers: { periodDays: number | null; enabled: number | null; active: number | null; reportRefreshDate: string | null } | null
}

export interface AiGovSnapshot {
  tenantId: string
  tenantName?: string
  /** null = never collected. */
  fetchedAt: string | null
  agents: AiAgent[]
  sources: AiGovSourceState[]
  summary: AiAgentSummary
  /** null = Copilot licences never collected. */
  copilot: CopilotAdoption | null
  /** Conditional Access agent targeting (beta). null = never read. */
  caAgents: { fetchedAt: string; policies: Array<{ id: string; displayName: string | null; state: string | null; targetsAgents: boolean; undocumentedAgentFields: string[] }> } | null
  risk: AgentRisk | null
  checks: AiGovCheck[]
  /** AI licence-gap rows (AIG-052); [] when the licences were never read. */
  licenceGaps: LicenceGapRow[]
  /** Customer view (AIG-050): preview-API sections were removed; the page says so. */
  previewHidden?: boolean
}

/** One AI control's state from the latest AlphaBOLD / NIST AI RMF run, and its sign-off (AIG-050). */
export interface AiControlStatus {
  ref: string
  functionLabel: string
  requirement: string
  status: string
  outcome: string | null
  attested: boolean
}
export interface AiControlStatusView {
  run: { id: string; frameworkId: string; createdAt: string } | null
  controls: AiControlStatus[]
}
export const getAiControlStatus = (tenantId: string): Promise<AiControlStatusView> =>
  api.get<Envelope<AiControlStatusView>>(`/api/ai-governance/${tenantId}/controls`).then((r) => r.data.data)

/** One AI capability and its licence (AIG-052). `unknown` = not read, never a gap. */
export interface LicenceGapRow {
  capability: 'copilot' | 'auditPremium' | 'sam'
  label: string
  state: 'licensed' | 'not_licensed' | 'unknown'
  controls: string[]
  unlocks: string
  /** listPriceUsd: indicative list price, USD per user per month; null = no reference price. */
  unlockedBy: Array<{ skuPartNumber: string | null; name: string; listPriceUsd: number | null; note?: string }>
}

export interface AiGovTenantSummary extends AiAgentSummary {
  tenantId: string
  tenantName: string
  fetchedAt: string | null
  partial: boolean
  copilot: Pick<CopilotAdoption, 'footprint' | 'purchased' | 'assigned'> | null
  /** Customer view: the agent counts are preview data and were zeroed. */
  previewHidden?: boolean
}

/** AI workload register (AIG-041). Risk tiers decided 2026-10-27: high / medium / low. */
export type RiskTier = 'high' | 'medium' | 'low'
export interface AiWorkloadInput {
  name: string
  purpose?: string | null
  dataSources?: string | null
  owner?: string | null
  riskTier: RiskTier
  assumptions?: string | null
  limitations?: string | null
  /** YYYY-MM-DD */
  lastReviewAt?: string | null
  nextReviewAt?: string | null
}
export interface AiWorkload extends AiWorkloadInput {
  id: string
  tenantId: string
  createdBy: string | null
  createdAt: string
  updatedBy: string | null
  updatedAt: string | null
}
export const getAiWorkloads = (tenantId: string): Promise<AiWorkload[]> =>
  api.get<Envelope<AiWorkload[]>>(`/api/ai-governance/${tenantId}/workloads`).then((r) => r.data.data ?? [])
export const createAiWorkload = (tenantId: string, body: AiWorkloadInput): Promise<AiWorkload> =>
  api.post<Envelope<AiWorkload>>(`/api/ai-governance/${tenantId}/workloads`, body).then((r) => r.data.data)
export const updateAiWorkload = (tenantId: string, id: string, body: AiWorkloadInput): Promise<AiWorkload> =>
  api.put<Envelope<AiWorkload>>(`/api/ai-governance/${tenantId}/workloads/${encodeURIComponent(id)}`, body).then((r) => r.data.data)
export const deleteAiWorkload = (tenantId: string, id: string): Promise<void> =>
  api.delete(`/api/ai-governance/${tenantId}/workloads/${encodeURIComponent(id)}`).then(() => undefined)

export const getAiGovernanceSummary = (): Promise<AiGovTenantSummary[]> =>
  api.get<Envelope<AiGovTenantSummary[]>>('/api/ai-governance/summary').then((r) => r.data.data ?? [])

export const getTenantAiGovernance = (tenantId: string): Promise<AiGovSnapshot> =>
  api.get<Envelope<AiGovSnapshot>>(`/api/ai-governance/${tenantId}`).then((r) => r.data.data)

// Background refresh: 202 + poll getAiGovRefreshStatus until !running.
export const refreshTenantAiGovernance = (tenantId: string): Promise<{ tenantId: string; status: string }> =>
  api.post<Envelope<{ tenantId: string; status: string }>>(`/api/ai-governance/${tenantId}/refresh`).then((r) => r.data.data)

export const getAiGovRefreshStatus = (tenantId: string): Promise<AppCredRefreshStatus> =>
  api.get<Envelope<AppCredRefreshStatus>>(`/api/ai-governance/${tenantId}/refresh/status`).then((r) => r.data.data)

// ── Teams governance hub (backend/src/services/teamsGovernanceService.ts) ─────────────
// Absent is not empty throughout: null means "not read", only a list that was read is empty.

export type GroupKind = 'microsoft365' | 'security' | 'mailSecurity' | 'distribution' | 'other'
export type SkipReason = 'not_licensed' | 'not_consented' | 'not_collected' | 'preview_unavailable' | 'no_ai_footprint'

export interface GroupOwner {
  id: string
  displayName: string | null
  userPrincipalName: string | null
  /** null = not returned: never read as disabled. */
  accountEnabled: boolean | null
}

export interface TeamDetail {
  isArchived: boolean | null
  guestsCanCreateUpdateChannels: boolean | null
  guestsCanDeleteChannels: boolean | null
  membersCanCreateUpdateChannels: boolean | null
  membersCanDeleteChannels: boolean | null
  ownersCount: number | null
  membersCount: number | null
  guestsCount: number | null
}

export interface TeamFacts {
  detail: TeamDetail | null
  channels: { standard: number; private: number; shared: number } | null
  guestIds: string[] | null
  activity: { lastActivityDate: string | null; activeUsers: number | null; messages: number | null } | null
}

export interface TeamsGovGroup {
  id: string
  displayName: string | null
  description: string | null
  mail: string | null
  kind: GroupKind
  isTeam: boolean
  visibility: string | null
  membership: 'assigned' | 'dynamic'
  roleAssignable: boolean
  onPremisesSynced: boolean
  createdAt: string | null
  owners: GroupOwner[] | null
  ownersCapped: boolean
  labels: string[] | null
  team: TeamFacts | null
}

export interface TeamsGovGuest {
  id: string
  displayName: string | null
  mail: string | null
  userPrincipalName: string | null
  domain: string | null
  accountEnabled: boolean | null
  invitationState: string | null
  createdAt: string | null
  lastSignIn: string | null
  teamIds: string[]
}

export interface GuestDomainRow { domain: string; guests: number; teams: number; consumer: boolean }

export type CrossTenantAccess = 'open' | 'blocked' | 'scoped' | 'unknown'

export interface TeamsGovSettings {
  guestInvite: { allowInvitesFrom: string | null; guestAccess: 'same_as_members' | 'limited' | 'restricted' | 'unknown' } | null
  crossTenant: {
    b2bCollaborationInbound: CrossTenantAccess
    b2bCollaborationOutbound: CrossTenantAccess
    b2bDirectConnectInbound: CrossTenantAccess
    b2bDirectConnectOutbound: CrossTenantAccess
    partnerCount: number | null
    partnersWithDirectConnect: number | null
  } | null
}

export interface TeamsGovSummary {
  groups: number
  teams: number
  microsoft365: number
  security: number
  mailSecurity: number
  distribution: number
  dynamicGroups: number
  publicTeams: number
  ownerlessTeams: number | null
  singleOwnerTeams: number | null
  archivedTeams: number | null
  teamsWithGuests: number | null
  guests: number | null
  guestDomains: number | null
}

export interface TeamsGovSourceState {
  key: string
  label: string
  consented: boolean
  skipReason: SkipReason | null
  unavailableReason: string | null
  lastError: string | null
  fetchedAt: string | null
  truncated?: boolean
}

export interface TeamsFinding {
  id: string
  title: string
  outcome: 'pass' | 'partial' | 'fail' | 'skipped'
  skipReason: SkipReason | null
  detail: string
  affected: string[]
}

export type TeamsPolicyFamily = 'meeting' | 'appSetup' | 'appPermission' | 'events' | 'channels'
  | 'federation' | 'guestMessaging' | 'guestCalling' | 'messaging' | 'update'

export interface TeamsPolicySetting { key: string; label: string; value: string | number | boolean | null; risky: boolean; why: string | null }

export interface TeamsGovPolicy {
  identity: string
  isGlobal: boolean
  settings: TeamsPolicySetting[]
  riskyCount: number
  differsFromGlobal: string[] | null
  weakerThanGlobal: string[] | null
  assignedUsers: number | null
  assignedUsersCapped: boolean
  assignedGroups: number | null
}

export interface TeamsPolicyFamilyView {
  family: TeamsPolicyFamily
  label: string
  tenantWide: boolean
  collected: boolean
  readError: string | null
  policies: TeamsGovPolicy[]
}

export interface TeamsGovPolicyInventory {
  collected: boolean
  collectedAt: string | null
  families: TeamsPolicyFamilyView[]
  assignmentsCollected: boolean
  assignmentsReadError: string | null
  unknownProperties: string[]
}

export interface TeamsGovSnapshot {
  tenantId: string
  tenantName?: string
  fetchedAt: string | null
  groups: TeamsGovGroup[]
  guests: TeamsGovGuest[] | null
  guestDomains: GuestDomainRow[] | null
  settings: TeamsGovSettings
  meta: { activityConcealed: boolean; activityMatched: number | null; teamsBeyondCap: number }
  sources: TeamsGovSourceState[]
  summary: TeamsGovSummary
  findings: TeamsFinding[]
  policies: TeamsGovPolicyInventory | null
  /** Messaging and update policies as the Compliance hub stores them (fallback). */
  teamsPolicies: {
    messaging: TeamsMessagingPolicy[] | null
    update: TeamsUpdatePolicy[] | null
    source: { fetchedAt: string | null; unavailableReason: string | null; lastError: string | null } | null
  }
}

export interface TeamsGovTenantSummary extends TeamsGovSummary {
  tenantId: string
  tenantName: string
  fetchedAt: string | null
  policiesFetchedAt: string | null
  partial: boolean
}

export const getTeamsGovernanceSummary = (): Promise<TeamsGovTenantSummary[]> =>
  api.get<Envelope<TeamsGovTenantSummary[]>>('/api/teams-governance/summary').then((r) => r.data.data ?? [])

export const getTenantTeamsGovernance = (tenantId: string): Promise<TeamsGovSnapshot> =>
  api.get<Envelope<TeamsGovSnapshot>>(`/api/teams-governance/${tenantId}`).then((r) => r.data.data)

// Background refresh: 202 + poll getTeamsGovRefreshStatus until !running.
export const refreshTenantTeamsGovernance = (tenantId: string): Promise<{ tenantId: string; status: string }> =>
  api.post<Envelope<{ tenantId: string; status: string }>>(`/api/teams-governance/${tenantId}/refresh`).then((r) => r.data.data)

export const getTeamsGovRefreshStatus = (tenantId: string): Promise<AppCredRefreshStatus> =>
  api.get<Envelope<AppCredRefreshStatus>>(`/api/teams-governance/${tenantId}/refresh/status`).then((r) => r.data.data)

/** What the server says about reminder mail — the page may only claim what this reports. */
export interface AppCredReminderStatus {
  enabled: boolean
  dryRun: boolean
  hour: number
  timeZone: string
  timeZoneLabel: string
  /** Configuration gaps; only returned to internal (MSP) users. */
  problems: string[]
  lastRun: { runDate: string; status: string; finishedAt: string | null } | null
}

export const getAppCredReminderStatus = (): Promise<AppCredReminderStatus> =>
  api.get<Envelope<AppCredReminderStatus>>('/api/app-credentials/reminders/status').then((r) => r.data.data)

/** Appendix C CSV inventory for one tenant (409 when never collected). */
export const exportAppCredentialsCsv = (tenantId: string, tenantName: string): Promise<void> =>
  downloadBlob(
    `/api/app-credentials/${tenantId}/export/csv`,
    `entra-credential-inventory-${tenantName.replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 40)}-${new Date().toISOString().slice(0, 10)}.csv`
  )

// ── Compliance & DLP hub (E13) ────────────────────────────────────────────────
// Read-only. There is deliberately NO refresh call: DLP and retention are collected by
// the nightly runner and cannot be refreshed on demand, so a refresh helper here would
// invite a button that lies about what it does.

export interface ComplianceLocations {
  exchange: number
  sharePoint: number
  oneDrive: number
  teams: number
  endpoint: number
  skype?: number
  /** Microsoft 365 Copilot location (DLP only); null or absent = not read. */
  copilot?: number | null
}

export interface DlpPolicy {
  name: string
  guid: string | null
  mode: string
  rawMode: string | null
  enabled: boolean
  enforcing: boolean
  priority: number | null
  createdAt: string | null
  changedAt: string | null
  workload: string | null
  locations: ComplianceLocations
  tenantWide: boolean
  coversAnyWorkload: boolean
  ruleCount: number
  enabledRuleCount: number
  sensitiveInfoTypes: string[]
}

export interface RetentionPolicy {
  name: string
  guid: string | null
  enabled: boolean
  mode: string | null
  createdAt: string | null
  changedAt: string | null
  locations: ComplianceLocations
  tenantWide: boolean
  coversAnyWorkload: boolean
}

/** A Communication Compliance (AB-IG-03) supervisory-review policy. */
export interface CommCompliancePolicy {
  name: string | null
  enabled: boolean
}

/** A Teams messaging policy (AB-TM-05). */
export interface TeamsMessagingPolicy {
  identity: string | null
  allowUserDelete: boolean
  allowOwnerDelete: boolean
  allowUserEdit: boolean
}

/**
 * A Teams update-ring policy (AB-TM-06). `forced` is true only when publicPreview or
 * privatePreview is the literal string "Forced" - "Enabled" merely offers the ring
 * switcher to the user and is not itself a gap.
 */
export interface TeamsUpdatePolicy {
  identity: string | null
  publicPreview: string | null
  privatePreview: string | null
  forced: boolean
}

export interface ComplianceSourceState {
  key: string
  label: string
  consented: boolean
  unavailableReason: string | null
  lastError: string | null
  fetchedAt: string | null
  truncated?: boolean
}

export interface ComplianceFinding {
  id: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  area: string
  title: string
  detail: string
  affected: string[]
}

export interface ComplianceSummary {
  dlpTotal: number
  dlpEnforcing: number
  dlpTestMode: number
  dlpDisabled: number
  dlpNoRules: number
  dlpNoWorkload: number
  retentionTotal: number
  retentionEnabled: number
  labelsTotal: number
  labelsPublished: number
  // Communication Compliance (AB-IG-03) and Teams messaging/update policies (AB-TM-05/06).
  // Mirrors the backend ComplianceSummary field-for-field - see complianceService.ts.
  commTotal: number
  commEnabled: number
  teamsMsgTotal: number
  teamsMsgPermissive: number
  teamsUpdateTotal: number
  teamsUpdateForced: number
  critical: number
  high: number
  medium: number
  low: number
}

/** A sensitivity label policy: the labels it publishes, and member counts (-1 = all). */
export interface LabelPolicy {
  name: string | null
  guid: string | null
  enabled: boolean
  labels: string[]
  userCount: number
  groupCount: number
}

export interface ComplianceSnapshot {
  tenantId: string
  tenantName?: string
  /** Graph leg. Ages independently of runnerFetchedAt - never merge the two in the UI. */
  fetchedAt: string | null
  /** Runner leg (DLP, retention). Nightly at best. */
  runnerFetchedAt: string | null
  dlp: DlpPolicy[]
  retention: RetentionPolicy[]
  // `isEnabled`, matching the live Graph API. The deprecated informationProtection API
  // used `isActive`; reading that name against the new payload gives undefined for
  // every label, i.e. 'nothing published' on every tenant.
  // `hasProtection`: encryption or do-not-forward. null = Graph did not say (AIG-001).
  labels: Array<{ id: string | null; name: string | null; isEnabled: boolean; hasProtection?: boolean | null }>
  /** Sensitivity label policies, from the runner. Same null/[] contract as commCompliance. */
  labelPolicies: LabelPolicy[] | null
  /**
   * Communication Compliance policies (AB-IG-03). null = never collected; [] = collected,
   * tenant has none. An empty array is a scored finding - never default a null response
   * field to [] when rendering, or a never-collected tenant reads as a clean one.
   */
  commCompliance: CommCompliancePolicy[] | null
  /** Teams messaging policies (AB-TM-05). Same null/[] contract as commCompliance. */
  teamsMessaging: TeamsMessagingPolicy[] | null
  /** Teams update-ring policies (AB-TM-06). Same null/[] contract as commCompliance. */
  teamsUpdate: TeamsUpdatePolicy[] | null
  findings: ComplianceFinding[]
  sources: ComplianceSourceState[]
  summary: ComplianceSummary
}

export interface ComplianceMeta {
  runnerCollected: boolean
  graphCollected: boolean
  totalDlp: number
  totalFindings: number
}

export const getTenantCompliance = (
  tenantId: string
): Promise<{ snapshot: ComplianceSnapshot; meta: ComplianceMeta }> =>
  api
    .get<Envelope<ComplianceSnapshot> & { meta: ComplianceMeta }>(`/api/compliance/${tenantId}`)
    .then((r) => ({ snapshot: r.data.data, meta: r.data.meta }))

export interface ComplianceRollupRow {
  tenantId: string
  tenantName: string
  fetchedAt: string | null
  runnerFetchedAt: string | null
  /** false = never collected. NOT the same as "no DLP policies" - do not render zeros. */
  collected: boolean
  summary: ComplianceSummary
}

export const getComplianceRollup = (): Promise<ComplianceRollupRow[]> =>
  api.get<Envelope<ComplianceRollupRow[]>>('/api/compliance/summary').then((r) => r.data.data)

export const getCaRefreshStatus = (tenantId: string): Promise<CaRefreshStatus> =>
  api.get<Envelope<CaRefreshStatus>>(`/api/conditional-access/${tenantId}/refresh/status`).then((r) => r.data.data)

// Exports go through downloadBlob like the assessment ones: the filename comes from the
// server's Content-Disposition in spirit, but axios does not expose it reliably across
// browsers, so it is rebuilt here to match.
export const exportConditionalAccessPdf = (tenantId: string, tenantName: string): Promise<void> =>
  downloadBlob(
    `/api/conditional-access/${tenantId}/export/pdf`,
    `conditional-access-${tenantName.replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 40)}.pdf`
  )

export const exportConditionalAccessXlsx = (tenantId: string, tenantName: string): Promise<void> =>
  downloadBlob(
    `/api/conditional-access/${tenantId}/export/xlsx`,
    `conditional-access-${tenantName.replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 40)}.xlsx`
  )

export const exportConditionalAccessJson = (tenantId: string, tenantName: string): Promise<void> =>
  downloadBlob(
    `/api/conditional-access/${tenantId}/export/json`,
    `conditional-access-${tenantName.replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 40)}.json`
  )



// ── Collection freshness (AB #113227) ────────────────────────────────────────
// Backs the status badge in the app shell. Scope-aware on the server: internal principals
// get fleet state, customers get only their own tenants. The client renders whatever
// `scope` comes back as - it never decides which view it is entitled to.

export type CollectionState = 'collecting' | 'degraded' | 'stalled' | 'unknown'

export interface CollectionScanStep {
  tenant: string
  step: string
  message?: string
  reason?: string
}

export interface CollectionStatus {
  scope: 'fleet' | 'tenant' | 'unknown'
  state: CollectionState
  label: string
  detail: string
  ageHours: number | null
  staleAfterHours: number
  running?: boolean
  lastRun?: {
    trigger: string
    startedAt: string
    finishedAt: string | null
    tenants: number
    counters: Record<string, unknown>
    errors: CollectionScanStep[]
    skipped: CollectionScanStep[]
  } | null
  tenants?: Array<{ tenantId: string; tenantName: string; lastCollectedAt: string | null }>
}

export const getCollectionStatus = (): Promise<CollectionStatus> =>
  api.get<Envelope<CollectionStatus>>('/api/collection-status').then((r) => r.data.data)

export default api
